import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FALLBACK_GENERATOR_OWNED_COLUMNS,
  isTemplateOwnedRow
} from "../../../helpers/generatorOwnedColumns";
import { csvEscape, parseCsvRows, readCsvHeader } from "../../../helpers/csv";
import {
  applyTargetNames,
  describeTargetNameImport,
  readTargetNamePairs
} from "../../../helpers/targetNameImport";
import { columnRowBucket, compareColumnRows } from "../../../helpers/columnMappingOrder";
import {
  describePending,
  pendingChanges,
  signatureOf
} from "../../../helpers/pendingChanges";
import { fetchGeneratorOwnedColumns } from "../../../services/dataVaultModelling.service";

import { formatLabel } from "../model/formatting";
import type {
  ColumnMappingRecord,
  SelectedEntity,
  TableMappingRecord
} from "../model/types";
import {
  fetchSourceEntityColumns,
  saveSourceEntityColumnMappings,
  type DeleteColumnMappingPayload,
  type SaveColumnMappingPayload,
  type SourceEntityColumnMapping,
  type SourceEntityColumnsResponse
} from "../../../services/dataVaultModelling.service";
import { ColumnMappingActionCard } from "../../../components/modelling/ColumnMappingActionCard";
import { ColumnMappingModalShell } from "../../../components/modelling/ColumnMappingModalShell";
import { SqlEditorField } from "./forms/SqlEditorField";
import { SqlPreview } from "./forms/SqlPreview";
import { isMetadataSourceColumn } from "../../../helpers/metadataColumns";
import { getHashColumnBudget } from "../../../helpers/hashColumnBudget";
import { getScopeCustomerId, getScopeEnvironmentId } from "../../../services/activeScope";

const parseNumericOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const tableInputClass =
  "tw-h-7 tw-w-full tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-2.5 tw-text-xs tw-text-slate-800 focus:tw-border-[#03234a] focus:tw-outline-none";


/** Picker value meaning "this inherited key is a constant here, not a source column". */
const CONSTANT_SOURCE_SENTINEL = "__constant__";

export function MappingModal({
  open,
  onClose,
  columns,
  tableMappings,
  selectedEntity
}: {
  open: boolean;
  onClose: () => void;
  columns: ColumnMappingRecord[];
  tableMappings: TableMappingRecord[];
  selectedEntity: SelectedEntity | null;
}): JSX.Element | null {
  const [columnRows, setColumnRows] = useState<SourceEntityColumnMapping[]>([]);
  const targetNameFileRef = useRef<HTMLInputElement | null>(null);
  const [targetNameImportReport, setTargetNameImportReport] = useState<
    { message: string; unmatched: string[] } | null
  >(null);
  const [inheritedColumnRows, setInheritedColumnRows] = useState<SourceEntityColumnMapping[]>([]);
  /**
   * A LINK's stored business keys that no hub key claims any more — a hub dropped the key
   * after the link was modelled. A link renders only seeds, so without these rows such a key
   * is invisible and nothing can take it off.
   */
  const [staleLinkKeyRows, setStaleLinkKeyRows] = useState<SourceEntityColumnMapping[]>([]);
  const [parentHubName, setParentHubName] = useState<string | null>(null);
  // The entity's own source columns, kept even for links (which do not list their columns
  // as rows) so the canonical-key picker always has something to offer.
  const [sourceColumnOptions, setSourceColumnOptions] = useState<SourceEntityColumnMapping[]>([]);
  const [columnLoading, setColumnLoading] = useState(false);
  const [columnError, setColumnError] = useState<string | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  type EditableField = "target_entity_rdv_column_transform" | "description";
  const [fieldEditor, setFieldEditor] = useState<{
    rowKey: string;
    value: string;
    title: string;
    field: EditableField;
    label: string;
  } | null>(null);
  const [isSavingColumns, setIsSavingColumns] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(null);
  const [reloadColumnsKey, setReloadColumnsKey] = useState(0);
  /** Non-blocking "this will not join yet" notes returned by the save. */
  const [saveWarnings, setSaveWarnings] = useState<string[]>([]);
  /**
   * What the grid looked like when it loaded, so a row can say whether it is stored or
   * merely proposed. Without this an inherited key arrives pre-filled and indistinguishable
   * from something already saved — which is exactly how one got left unsaved.
   */
  const [loadedSignatures, setLoadedSignatures] = useState<Map<string, string>>(new Map());
  const [showOnlySelectedRows, setShowOnlySelectedRows] = useState(false);
  const [rowSearchQuery, setRowSearchQuery] = useState("");
  const [rowViewFilter, setRowViewFilter] = useState<"all" | "mapped" | "unmapped" | "inherited">("all");
  const FALLBACK_ENVIRONMENT_ID = "114";
  const FALLBACK_CUSTOMER_ID = "101";
  // The generator owns these column names; the list is served from its own constant so the
  // blade cannot drift from it, with the bundled set standing in until the call returns.
  const [generatorOwnedColumns, setGeneratorOwnedColumns] = useState<Set<string>>(
    FALLBACK_GENERATOR_OWNED_COLUMNS
  );
  useEffect(() => {
    let cancelled = false;
    fetchGeneratorOwnedColumns().then((served) => {
      if (!cancelled && served.size) {
        setGeneratorOwnedColumns(served);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const isHubEntity = selectedEntity?.type === "hub";
  const isLinkEntity = selectedEntity?.type === "link";
  const [hubHashKeyName, setHubHashKeyName] = useState("");
  const [hubBusinessKeyOrder, setHubBusinessKeyOrder] = useState<string[]>([]);
  const linkBusinessKeyOptions = useMemo(() => {
    if (!selectedEntity || selectedEntity.type !== "link") {
      return [];
    }
    const linkedHubs = selectedEntity.entity.linkedHubs ?? [];
    const seen = new Set<number>();
    const options: Array<{ id: number; name: string }> = [];
    linkedHubs.forEach((hub) => {
      const parsedId = parseNumericOrNull(hub.hub_target_entity_rdv_id);
      if (!parsedId || seen.has(parsedId)) {
        return;
      }
      const labelSource = hub.hub_target_entity_rdv_name ?? "";
      const name = formatLabel(labelSource) || labelSource || `Hub ${parsedId}`;
      seen.add(parsedId);
      options.push({ id: parsedId, name });
    });
    return options;
  }, [selectedEntity]);
  const linkHubNameById = useMemo(() => {
    const map = new Map<number, string>();
    linkBusinessKeyOptions.forEach((option) => map.set(option.id, option.name));
    return map;
  }, [linkBusinessKeyOptions]);
  const requireLinkBusinessKeySelection = isLinkEntity && linkBusinessKeyOptions.length >= 2;
  const showBusinessKeyForColumn = isLinkEntity;
  // Counter, not a timestamp or a random: row keys must be stable and reproducible.
  const nextConstantRowId = useRef(1);
  const buildRowKey = (row: SourceEntityColumnMapping) =>
    // A brand-new constant has no ids at all and its name is still being typed, so it
    // carries its own stable key. Without one every new constant keys as "na-na".
    row.client_row_key
      ? row.client_row_key
      : // A canonical-key seed carries neither id yet, so key it by the hub column it stands
      // for — otherwise every seed shares the key "na-na" and edits hit the wrong row.
      row.is_canonical_key
      ? // Key on the HUB COLUMN this seed realises, never on its target name: the name is
        // editable now, and deriving the key from it changed the key on every keystroke,
        // which remounted the input and threw focus away after each character.
        `seed-${row.inherited_from_target_entity_rdv_id ?? "na"}-${
          row.business_key_for_target_entity_rdv_column_id ??
          `n:${(row.target_entity_rdv_column_name ?? "").toLowerCase()}`
        }`
      : `${row.source_entity_column_id ?? "na"}-${row.target_entity_rdv_column_id ?? "na"}`;
  const isSeedRowKey = (rowKey: string) => rowKey.startsWith("seed-");
  /** Everything about a row a modeller can change, reduced to one comparable string. */
  const rowContentSignature = (row: SourceEntityColumnMapping) =>
    signatureOf([
      row.source_entity_column_name,
      row.target_entity_rdv_column_name,
      row.business_key,
      row.business_key_order,
      row.hash_key,
      row.pii,
      row.description,
      row.target_entity_rdv_column_transform,
      row.is_constant
    ]);
  // md_* source columns are platform-owned: the row is shown but never editable.
  const canToggleModeledState = (row: SourceEntityColumnMapping) =>
    !Boolean(row.is_inherited || row.is_locked);
  /**
   * Descriptions document the source column definition returned by the API. Keep those
   * descriptions as supplied for fetched rows, while allowing descriptions on rows created in
   * this modal (custom columns and newly generated hash outputs).
   */
  const isNewCustomMappingRow = (row: SourceEntityColumnMapping) => {
    if (row.client_row_key) {
      return true;
    }
    if (row.is_canonical_key || row.is_inherited || row.is_locked) {
      return false;
    }
    return [
      row.source_target_entity_rdv_column_mapping_id,
      row.source_entity_column_id,
      row.target_entity_rdv_column_id
    ].every((value) => parseNumericOrNull(value) == null);
  };
  const isTrueFlag = (value: string | boolean | null | undefined): boolean => {
    if (typeof value === "boolean") {
      return value;
    }
    if (!value) {
      return false;
    }
    const normalized = value.toString().trim().toUpperCase();
    return normalized === "Y" || normalized === "TRUE";
  };
  const isBusinessKeyTrue = (value: string | boolean | null | undefined): boolean =>
    isTrueFlag(value);
  const isHashKeyTrue = (value: string | boolean | null | undefined): boolean =>
    isTrueFlag(value);
  const resetSaveFeedback = () => {
    setSaveErrorMessage(null);
    setSaveSuccessMessage(null);
    setSaveWarnings([]);
  };
  const selectedEntityType = selectedEntity?.type;
  const selectedEntityId = selectedEntity?.entity.id ?? null;
  const selectedSourceEntityId = selectedEntity?.entity.record.source_entity_id ?? null;
  const selectedTargetEntityRdvId = selectedEntity?.entity.record.target_entity_rdv_id ?? null;

  useEffect(() => {
    if (!open || !selectedEntity) {
      setColumnRows([]);
      setInheritedColumnRows([]);
      setStaleLinkKeyRows([]);
      setColumnError(null);
      return undefined;
    }
    const sourceEntityIdRaw = selectedEntity.entity.record.source_entity_id;
    const sourceEntityId = sourceEntityIdRaw ? Number(sourceEntityIdRaw) : null;
    if (!sourceEntityId) {
      setColumnRows([]);
      setInheritedColumnRows([]);
      setStaleLinkKeyRows([]);
      setColumnError("No source entity reference available for this table.");
      return undefined;
    }
    const environmentId =
      typeof window !== "undefined"
        ? getScopeEnvironmentId() ?? FALLBACK_ENVIRONMENT_ID
        : FALLBACK_ENVIRONMENT_ID;
    const customerId =
      typeof window !== "undefined"
        ? getScopeCustomerId() ?? FALLBACK_CUSTOMER_ID
        : FALLBACK_CUSTOMER_ID;
    const params = {
      source_entity_id: sourceEntityId,
      environment_id: Number(environmentId),
      customer_id: Number(customerId),
      for_modelling: true,
      target_entity_rdv_id: selectedEntity.entity.record.target_entity_rdv_id
        ? Number(selectedEntity.entity.record.target_entity_rdv_id)
        : undefined
    };
    let isMounted = true;
    setColumnLoading(true);
    setColumnError(null);
    fetchSourceEntityColumns(params)
      .then((response) => {
        if (!isMounted) {
          return;
        }
        const normalizedResponse: SourceEntityColumnsResponse | null = Array.isArray(response)
          ? null
          : response;
        const responseRows = Array.isArray(response)
          ? response
          : Array.isArray(normalizedResponse?.items)
            ? normalizedResponse.items
            : [];

        const inheritedRowsSource = isLinkEntity
          ? normalizedResponse?.inherited_link_business_key_items
          : normalizedResponse?.inherited_business_key_items;
        // What this entity stores itself, by target column. A seed for a key the entity
        // already carries arrives with the entity's ids, source and transform, but with the
        // HUB's name, description and pii — the backend builds it from the hub's column. Shown
        // as-is, a satellite key stored under its own name read as the hub's, and saving
        // wrote the hub's values over the entity's own.
        const storedByColumnId = new Map<number, SourceEntityColumnMapping>();
        responseRows.forEach((row: SourceEntityColumnMapping) => {
          const columnId = parseNumericOrNull(row.target_entity_rdv_column_id);
          if (columnId != null) {
            storedByColumnId.set(columnId, row);
          }
        });
        const inheritedRows = Array.isArray(inheritedRowsSource)
          ? inheritedRowsSource.map((row) => {
              const stored = isTrueFlag(row.already_mapped)
                ? storedByColumnId.get(parseNumericOrNull(row.target_entity_rdv_column_id) ?? -1)
                : undefined;
              return {
                ...row,
                ...(stored
                  ? {
                      target_entity_rdv_column_name:
                        stored.target_entity_rdv_column_name?.trim() ||
                        row.target_entity_rdv_column_name,
                      pii: stored.pii
                    }
                  : {}),
                description: (stored ? stored.description : row.description) ?? "",
                business_key: row.business_key ?? true,
                target_entity_rdv_column_transform: row.target_entity_rdv_column_transform ?? "",
                is_inherited: true,
                is_locked: true
              };
            })
          : [];
        setParentHubName(normalizedResponse?.parent_hub?.hub_target_entity_rdv_name ?? null);
        const rows = responseRows.map((row: SourceEntityColumnMapping) => {
          // md_effective_ts used to be force-flagged as a business key on satellites.
          // The satellite template emits md_effective_ts itself, so modelling it produced
          // the column twice in the generated SQL, and the business-key flag dragged it
          // into the hub hash (breaking the sat -> hub join) and the HASHDIFF (every load
          // looked changed). Leave the stored value alone.
          const resolvedBusinessKey = row.business_key;
          const resolvedBusinessKeyForId = parseNumericOrNull(
            row.business_key_for_target_entity_rdv_id
          );
          const resolvedBusinessKeyForName =
            row.business_key_for_target_entity_rdv_name ??
            (resolvedBusinessKeyForId != null
              ? linkHubNameById.get(resolvedBusinessKeyForId) ?? null
              : null);
          return {
            ...row,
            // Keep what the API actually returned. The line below defaults an unmapped
            // row's target name to its source column name for display, which makes an
            // unmapped source column indistinguishable from a modelled one — and the seed
            // dedupe below must only count rows that are genuinely mapped.
            mapped_target_entity_rdv_column_name: row.target_entity_rdv_column_name ?? null,
            target_entity_rdv_column_name:
              row.target_entity_rdv_column_name ?? row.source_entity_column_name,
            description: row.description ?? "",
            business_key: resolvedBusinessKey,
            business_key_order: parseNumericOrNull(row.business_key_order),
            business_key_for_target_entity_rdv_id:
              resolvedBusinessKeyForId ?? parseNumericOrNull(row.business_key_for),
            business_key_for_target_entity_rdv_name: resolvedBusinessKeyForName,
            target_entity_rdv_column_transform: row.target_entity_rdv_column_transform ?? "",
            is_inherited: Boolean(row.is_inherited),
            is_locked: Boolean(row.is_locked)
          };
        });
        // A link's stored key that no seed took over: a hub dropped it after the link was
        // modelled. Only its removal is offered — see staleLinkKeyRows.
        const seedColumnIds = new Set(
          inheritedRows
            .map((row) => parseNumericOrNull(row.target_entity_rdv_column_id))
            .filter((id): id is number => id != null)
        );
        const staleLinkKeys = isLinkEntity
          ? rows.filter((row) => {
              const columnId = parseNumericOrNull(row.target_entity_rdv_column_id);
              return (
                columnId != null &&
                !seedColumnIds.has(columnId) &&
                isBusinessKeyTrue(row.business_key) &&
                !isHashKeyTrue(row.hash_key)
              );
            })
          : [];
        setColumnRows(isLinkEntity ? [] : rows);
        setSourceColumnOptions(rows);
        setInheritedColumnRows(inheritedRows);
        setStaleLinkKeyRows(staleLinkKeys);
        // Snapshot what was STORED. A seed the entity has not written yet is a proposal and
        // must read as pending — but one it ALREADY carries as its own business key is not.
        // The backend merges that column into the seed (`already_mapped`), so a link, which
        // renders nothing but seeds, opened with every row counted as unsaved work: the
        // modeller was told that closing would lose six rows he had never touched.
        // A seed-claimed row is represented by its seed; snapshotting both would count the
        // hidden row as removed.
        const storedRows = isLinkEntity
          ? staleLinkKeys
          : rows.filter(
              (row) =>
                !seedColumnIds.has(parseNumericOrNull(row.target_entity_rdv_column_id) ?? -1)
            );
        const storedSeeds = inheritedRows.filter((row) => isTrueFlag(row.already_mapped));
        setLoadedSignatures(
          new Map(
            [...storedRows, ...storedSeeds].map((row) => [
              buildRowKey(row),
              rowContentSignature(row)
            ])
          )
        );
        if (isHubEntity) {
          const orderedBusinessKeyRows = rows
            .filter((row) => isBusinessKeyTrue(row.business_key))
            .sort((left, right) => {
              const leftOrder = parseNumericOrNull(left.business_key_order) ?? Number.MAX_SAFE_INTEGER;
              const rightOrder =
                parseNumericOrNull(right.business_key_order) ?? Number.MAX_SAFE_INTEGER;
              return leftOrder - rightOrder;
            });
          setHubBusinessKeyOrder(orderedBusinessKeyRows.map((row) => buildRowKey(row)));
        }
        setSelectedRowIds(() => {
          if (isLinkEntity) {
            return new Set<string>();
          }
          const next = new Set<string>();
          rows.forEach((row: SourceEntityColumnMapping) => {
            const hasMappedTargetColumn = parseNumericOrNull(row.target_entity_rdv_column_id) != null;
            const hasExistingMapping =
              parseNumericOrNull(row.source_target_entity_rdv_column_mapping_id) != null;
            // md_effective_ts is no longer auto-modelled on satellites — the template
            // already emits it; modelling it duplicated the column in the generated SQL.
            if (
              isBusinessKeyTrue(row.business_key) ||
              hasMappedTargetColumn ||
              hasExistingMapping
            ) {
              next.add(buildRowKey(row));
            }
          });
          return next;
        });
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        const message =
          error instanceof Error ? error.message : "Unable to load source entity columns.";
        setColumnError(message);
        setColumnRows([]);
        setInheritedColumnRows([]);
        setStaleLinkKeyRows([]);
      })
      .finally(() => {
        if (isMounted) {
          setColumnLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [
    open,
    selectedEntityType,
    selectedEntityId,
    selectedSourceEntityId,
    selectedTargetEntityRdvId,
    reloadColumnsKey
  ]);

  useEffect(() => {
    if (!open) {
      setIsSavingColumns(false);
      setSaveErrorMessage(null);
      setSaveSuccessMessage(null);
      setShowOnlySelectedRows(false);
      setHubBusinessKeyOrder([]);
      setRowSearchQuery("");
      setRowViewFilter("all");
      return;
    }
    resetSaveFeedback();
  }, [open, selectedEntityId]);

  useEffect(() => {
    if (showOnlySelectedRows && selectedRowIds.size === 0) {
      setShowOnlySelectedRows(false);
    }
  }, [showOnlySelectedRows, selectedRowIds]);

  useEffect(() => {
    if (!open || !isHubEntity) {
      return;
    }
    // `??` falls through on null/undefined but NOT on "", and a hub that has not been named
    // yet holds an empty string — which produced a hash column literally called `_hash`
    // (env7 `financial_identity` in production). Treat blank as absent, and never derive a
    // name from nothing.
    const candidates = [
      selectedEntity?.entity.record.target_entity_rdv_name,
      selectedEntity?.entity.record.target_table
    ];
    const defaultTargetName =
      candidates.map((value) => (value ?? "").trim()).find((value) => value.length > 0) ??
      "hub";
    setHubHashKeyName(`${defaultTargetName}_hash`);
  }, [open, isHubEntity, selectedEntityId]);

  useEffect(() => {
    if (!isHubEntity) {
      return;
    }
    const currentBusinessKeyRowIds = columnRows
      .filter((row) => isBusinessKeyTrue(row.business_key))
      .map((row) => buildRowKey(row));
    setHubBusinessKeyOrder((previous) => {
      const previousSet = new Set(previous);
      const next = previous.filter((rowKey) => currentBusinessKeyRowIds.includes(rowKey));
      currentBusinessKeyRowIds.forEach((rowKey) => {
        if (!previousSet.has(rowKey)) {
          next.push(rowKey);
        }
      });
      return next;
    });
  }, [columnRows, isHubEntity]);

  useEffect(() => {
    if (!isHubEntity || !open) {
      return;
    }
    setColumnRows((previous) => {
      const hasBusinessKeys = previous.some((row) => isBusinessKeyTrue(row.business_key));
      const hashRows = previous.filter((row) => isHashKeyTrue(row.hash_key));
      if (!hasBusinessKeys) {
        return previous;
      }
      if (!hashRows.length) {
        const hashRow: SourceEntityColumnMapping = {
          source_target_entity_rdv_column_mapping_id: null,
          source_entity_column_id: null,
          source_entity_column_name: null,
          source_entity_id: selectedEntity?.entity.record.source_entity_id
            ? Number(selectedEntity.entity.record.source_entity_id)
            : null,
          source_entity_name: selectedEntity?.entity.record.source_entity_name ?? null,
          target_entity_rdv_id: selectedEntity?.entity.record.target_entity_rdv_id
            ? Number(selectedEntity.entity.record.target_entity_rdv_id)
            : null,
          target_entity_rdv_name: selectedEntity?.entity.record.target_table || null,
          target_schema: selectedEntity?.entity.record.target_schema || null,
          target_entity_rdv_column_id: null,
          target_entity_rdv_column_name: hubHashKeyName,
          business_key: false,
          business_key_order: null,
          business_key_for_target_entity_rdv_id: null,
          business_key_for_target_entity_rdv_name: null,
          hash_key: true,
          pii: false,
          description: "",
          target_entity_rdv_column_transform: "",
          is_inherited: false,
          is_locked: false
        };
        return [...previous, hashRow];
      }
      return previous.map((row) =>
        isHashKeyTrue(row.hash_key)
          ? { ...row, target_entity_rdv_column_name: hubHashKeyName }
          : row
      );
    });
  }, [hubHashKeyName, isHubEntity, open, selectedEntity]);

  useEffect(() => {
    if (!isHubEntity) {
      return;
    }
    const hashRowKeys = columnRows
      .filter((row) => isHashKeyTrue(row.hash_key))
      .map((row) => buildRowKey(row));
    if (!hashRowKeys.length) {
      return;
    }
    setSelectedRowIds((previous) => {
      const next = new Set(previous);
      hashRowKeys.forEach((rowKey) => next.add(rowKey));
      return next;
    });
  }, [columnRows, isHubEntity]);

  const searchNeedle = rowSearchQuery.trim().toLowerCase();
  const rowMatchesNeedle = (row: SourceEntityColumnMapping) => {
    if (!searchNeedle) {
      return true;
    }
    return [
      row.source_entity_column_name,
      row.target_entity_rdv_column_name,
      row.mapped_target_entity_rdv_column_name,
      row.source_entity_name,
      row.business_key_for_target_entity_rdv_name
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(searchNeedle);
  };

  // Only the MODELLED non-key rows reach CONCAT_WS: business keys partition the window and the
  // hash column is the output, not an input. An unmodelled row never reaches generation at all.
  const hashColumnBudget = useMemo(() => {
    if (selectedEntity?.type !== "satellite") {
      return null;
    }
    const record = selectedEntity.entity.record;
    const targetType =
      (record.dw_target_types_onprem ?? "").trim() || record.dw_target_types || null;
    const hashed = columnRows.filter(
      (row) =>
        selectedRowIds.has(buildRowKey(row)) &&
        !isBusinessKeyTrue(row.business_key) &&
        !isHashKeyTrue(row.hash_key)
    ).length;
    return getHashColumnBudget(targetType, hashed);
  }, [columnRows, selectedEntity, selectedRowIds]);

  const selectedCount = selectedRowIds.size;

  /**
   * Does a row match the search box?
   *
   * Shared with the INHERITED rows, which used not to be searched at all — and a link renders
   * ONLY those (`displayedRows` returns them wholesale), so typing in the box did nothing
   * whatever on a link, and on a satellite it filtered the source columns while leaving every
   * inherited business key on screen. Those are precisely the rows that carry a target name.
   */
  const filteredRows = useMemo(() => {
    return columnRows.filter((row) => {
      if (showOnlySelectedRows && !selectedRowIds.has(buildRowKey(row))) {
        return false;
      }
      if (rowViewFilter === "mapped") {
        if (!selectedRowIds.has(buildRowKey(row))) {
          return false;
        }
      } else if (rowViewFilter === "unmapped") {
        if (
          selectedRowIds.has(buildRowKey(row)) ||
          Boolean(row.is_inherited || row.is_locked)
        ) {
          return false;
        }
      } else if (rowViewFilter === "inherited") {
        return false;
      }
      return rowMatchesNeedle(row);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnRows, rowSearchQuery, rowViewFilter, selectedRowIds, showOnlySelectedRows]);

  // Every hub key is rendered as its seed, stored or not — a satellite included. A stored key
  // used to be dropped here in favour of the satellite's own row, which showed its source as
  // read-only text: once a key was saved, the column feeding it could never be changed again.
  // The seed edits that very column (it carries the column id), so the own row is hidden
  // instead — see seedClaimedColumnIds.
  //
  // The search box filters what is SHOWN only. What a seed claims and what a save writes are
  // read from `inheritedColumnRows`, so a search can neither expose a key's own row as a
  // stale key nor leave a seed out of the save.
  const visibleInheritedRows = useMemo(
    () => inheritedColumnRows.filter(rowMatchesNeedle),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inheritedColumnRows, rowSearchQuery]
  );

  /**
   * Target columns a seed has taken over. The seed carries the very column id, so saving it
   * writes that column — the entity's own row for it must not also be rendered, sent or
   * deleted, or the same column would be written twice with conflicting flags.
   */
  const seedClaimedColumnIds = useMemo(
    () =>
      new Set(
        inheritedColumnRows
          .map((row) => parseNumericOrNull(row.target_entity_rdv_column_id))
          .filter((id): id is number => id != null)
      ),
    [inheritedColumnRows]
  );
  const isSeedClaimed = (row: SourceEntityColumnMapping) =>
    seedClaimedColumnIds.has(parseNumericOrNull(row.target_entity_rdv_column_id) ?? -1);
  const staleLinkKeyRowKeys = useMemo(
    () => new Set(staleLinkKeyRows.map((row) => buildRowKey(row))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [staleLinkKeyRows]
  );

  /**
   * Which rows differ from what is stored. Seeds count as pending by construction — they
   * have never been written — and an edited or newly added row joins them.
   */
  const pendingByRowKey = useMemo(
    () =>
      pendingChanges(
        [
          // a seed-claimed row is not rendered or saved; its seed carries the change
          ...columnRows.filter((row) => !isSeedClaimed(row)),
          ...inheritedColumnRows,
          ...staleLinkKeyRows
        ],
        loadedSignatures,
        buildRowKey,
        rowContentSignature
      ),
    // buildRowKey / rowContentSignature are pure over their argument.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnRows, inheritedColumnRows, staleLinkKeyRows, seedClaimedColumnIds, loadedSignatures]
  );
  const pendingCount = pendingByRowKey.size;

  const displayedRows = useMemo(() => {
    if (isLinkEntity) {
      return [...visibleInheritedRows, ...staleLinkKeyRows.filter(rowMatchesNeedle)];
    }
    // Business keys, then hash keys, then what is modelled, then the rest of the source
    // table, and the template's own columns last — see helpers/columnMappingOrder.
    //
    // A column a visible seed has taken over is rendered ONLY as the seed: the seed edits
    // that same column, so showing both would be two rows for one column, and whichever
    // was saved last would win.
    const withIndex = filteredRows
      .filter(
        (row) =>
          !seedClaimedColumnIds.has(
            parseNumericOrNull(row.target_entity_rdv_column_id) ?? -1
          )
      )
      .map((row, index) => ({
      row,
      index,
      bucket: columnRowBucket({
        entityType: selectedEntity?.type,
        targetColumnName: row.target_entity_rdv_column_name,
        sourceColumnName: row.source_entity_column_name,
        isBusinessKey: isBusinessKeyTrue(row.business_key),
        isHashKey: isHashKeyTrue(row.hash_key),
        isModelled: selectedRowIds.has(buildRowKey(row)),
        reservedColumns: generatorOwnedColumns
      })
    }));
    withIndex.sort(compareColumnRows);
    const normalRows = withIndex.map(({ row }) => row);
    if (rowViewFilter === "inherited") {
      return visibleInheritedRows;
    }
    if (rowViewFilter === "unmapped") {
      return normalRows;
    }
    return [...visibleInheritedRows, ...normalRows];
  }, [
    filteredRows,
    visibleInheritedRows,
    staleLinkKeyRows,
    seedClaimedColumnIds,
    isLinkEntity,
    rowViewFilter,
    selectedEntity,
    selectedRowIds,
    generatorOwnedColumns
  ]);

  const hasDisplayRows = displayedRows.length > 0;
  const hubBusinessKeyRows = useMemo(() => {
    if (!isHubEntity) {
      return [];
    }
    const businessKeyRows = columnRows.filter((row) => isBusinessKeyTrue(row.business_key));
    const rowsByKey = new Map(
      businessKeyRows.map((row) => [buildRowKey(row), row] as const)
    );
    const orderedRows = hubBusinessKeyOrder
      .map((rowKey) => rowsByKey.get(rowKey))
      .filter((row): row is SourceEntityColumnMapping => Boolean(row));
    businessKeyRows.forEach((row) => {
      const rowKey = buildRowKey(row);
      if (!hubBusinessKeyOrder.includes(rowKey)) {
        orderedRows.push(row);
      }
    });
    return orderedRows;
  }, [columnRows, hubBusinessKeyOrder, isHubEntity]);
  const hubBusinessKeyIndexByRowKey = useMemo(() => {
    const map = new Map<string, number>();
    hubBusinessKeyRows.forEach((row, index) => {
      map.set(buildRowKey(row), index + 1);
    });
    return map;
  }, [hubBusinessKeyRows]);
  const hubHashKeyRows = useMemo(
    () => columnRows.filter((row) => isHashKeyTrue(row.hash_key)),
    [columnRows]
  );

  // Map the parent hub's business-key TARGET column -> its SOURCE column (lower-cased), from the
  // inherited rows. Used to decide whether a satellite/link BK transform is genuinely "drift" (same
  // source as the hub -> lock) or legitimate ALIGNMENT (the sat sources a DIFFERENT raw column and
  // must transform it to reproduce the hub's clean key -> allow editing). Custom-hub case: hub
  // customer_id <- customer_id (no transform) vs sat customer_id <- OKCUNO (CASE-WHEN cleanup).
  const hubBusinessKeySourceByTargetColumn = useMemo(() => {
    const map = new Map<string, string | null>();
    inheritedColumnRows.forEach((row) => {
      if (!isBusinessKeyTrue(row.business_key)) return;
      const tgt = row.target_entity_rdv_column_name?.trim().toLowerCase();
      if (!tgt) return;
      map.set(tgt, row.source_entity_column_name?.trim().toLowerCase() ?? null);
    });
    return map;
  }, [inheritedColumnRows]);

  const sourceEntityChips = useMemo(() => {
    const rowValues = columnRows
      .map((row) => row.source_entity_name?.trim())
      .filter((value): value is string => Boolean(value));
    const uniqueRowValues = Array.from(new Set(rowValues));
    if (uniqueRowValues.length) {
      return uniqueRowValues;
    }
    const fallback = selectedEntity?.entity.record.source_entity_name?.trim() ?? null;
    return fallback ? [fallback] : [];
  }, [columnRows, selectedEntity]);

  if (!open || !selectedEntity) {
    return null;
  }

  const isMetadataLockedRowKey = (rowKey: string) =>
    [...columnRows, ...inheritedColumnRows].some(
      (row) =>
        buildRowKey(row) === rowKey && isMetadataSourceColumn(row.source_entity_column_name)
    );

  const handleRowSelect = (row: SourceEntityColumnMapping) => {
    // A seed-claimed column is saved by its seed; un-modelling its own row would delete the
    // column the seed is writing in the same save.
    if (!canToggleModeledState(row) || isSeedClaimed(row)) {
      return;
    }
    const rowKey = buildRowKey(row);
    setSelectedRowIds((previous) => {
      const next = new Set(previous);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  const handleBulkModeledUpdate = (nextModeled: boolean) => {
    if (isLinkEntity) {
      return;
    }
    const eligibleRows = filteredRows.filter(
      (row) => canToggleModeledState(row) && !isSeedClaimed(row)
    );
    if (!eligibleRows.length) {
      return;
    }
    setSelectedRowIds((previous) => {
      const next = new Set(previous);
      eligibleRows.forEach((row) => {
        const rowKey = buildRowKey(row);
        if (nextModeled) {
          next.add(rowKey);
        } else {
          next.delete(rowKey);
        }
      });
      return next;
    });
  };

  const handleRowFieldChange = (
    rowKey: string,
    field: keyof SourceEntityColumnMapping,
    value: string | number | null
  ) => {
    if (isMetadataLockedRowKey(rowKey)) {
      return;
    }
    resetSaveFeedback();
    // A link's stale stored key offers one edit only: taking the business key off it.
    if (staleLinkKeyRowKeys.has(rowKey)) {
      if (
        field !== "business_key" &&
        field !== "business_key_order" &&
        field !== "business_key_for_target_entity_rdv_id" &&
        field !== "business_key_for_target_entity_rdv_name"
      ) {
        return;
      }
      setStaleLinkKeyRows((previous) =>
        previous.map((row) => (buildRowKey(row) === rowKey ? { ...row, [field]: value } : row))
      );
      return;
    }
    // Seeds live in their own state list. Their key and order stay canonical; the source
    // column, transform and name are the entity's own to set — and so, for a key the entity
    // already stores, are the description and pii its own row used to offer.
    if (isSeedRowKey(rowKey)) {
      setInheritedColumnRows((previous) =>
        previous.map((row) => {
          if (buildRowKey(row) !== rowKey) {
            return row;
          }
          if (
            (field === "description" || field === "pii") &&
            isTrueFlag(row.already_mapped)
          ) {
            return { ...row, [field]: value };
          }
          if (
            field !== "source_entity_column_name" &&
            field !== "target_entity_rdv_column_transform" &&
            field !== "target_entity_rdv_column_name"
          ) {
            return row;
          }
          if (field === "target_entity_rdv_column_name") {
            // The name is this entity's own to choose. Identity is the hub column the key
            // records itself as realising, which the seed carries and the save persists,
            // so renaming here cannot detach it from its hub key.
            return {
              ...row,
              target_entity_rdv_column_name: value == null ? "" : String(value)
            };
          }
          if (field === "source_entity_column_name") {
            // The hub's key may be a constant — SGDS's BKCC is, and each satellite stamps
            // its own literal ('DAHL' vs 'OPT'). The satellite therefore has to be able to
            // answer "this key has no source column here" as well as pick one.
            if (value === CONSTANT_SOURCE_SENTINEL) {
              return {
                ...row,
                is_constant: true,
                source_entity_column_name: null,
                source_entity_column_id: null
              };
            }
            const picked = sourceColumnOptions.find(
              (candidate) => candidate.source_entity_column_name === value
            );
            return {
              ...row,
              is_constant: false,
              source_entity_column_name: (value as string) || null,
              source_entity_column_id: picked?.source_entity_column_id ?? null
            };
          }
          // Only the transform is left; keep the field's string type explicit.
          return {
            ...row,
            target_entity_rdv_column_transform: value == null ? "" : String(value)
          };
        })
      );
      return;
    }
    setColumnRows((previous) =>
      previous.map((row) => {
        if (buildRowKey(row) !== rowKey) {
          return row;
        }
        const inheritedBusinessKey =
          (selectedEntity?.type === "satellite" || selectedEntity?.type === "link") &&
          (row.is_inherited || row.is_locked);
        if (
          inheritedBusinessKey &&
          (field === "source_entity_column_name" ||
            field === "target_entity_rdv_column_name" ||
            field === "business_key" ||
            field === "target_entity_rdv_column_transform")
        ) {
          return row;
        }
        return {
          ...row,
          [field]: value
        };
      })
    );
    if (field === "business_key") {
      setSelectedRowIds((previous) => {
        if (!isBusinessKeyTrue(value as string | boolean | null | undefined)) {
          return previous;
        }
        const next = new Set(previous);
        next.add(rowKey);
        return next;
      });
    }
  };

  const handleBusinessKeyForChange = (rowKey: string, value: string) => {
    resetSaveFeedback();
    const parsedId = parseNumericOrNull(value);
    const resolvedName = parsedId != null ? linkHubNameById.get(parsedId) ?? null : null;
    setColumnRows((previous) =>
      previous.map((row) =>
        buildRowKey(row) === rowKey
          ? {
            ...row,
            business_key_for_target_entity_rdv_id: parsedId,
            business_key_for_target_entity_rdv_name: resolvedName
          }
          : row
      )
    );
  };

  const handleOpenFieldEditor = (
    row: SourceEntityColumnMapping,
    field: EditableField,
    label: string
  ) => {
    setFieldEditor({
      rowKey: buildRowKey(row),
      value: (row[field] as string | null) ?? "",
      title: row.source_entity_column_name ?? label,
      field,
      label
    });
  };

  const handleFieldEditorChange = (value: string) => {
    setFieldEditor((previous) => (previous ? { ...previous, value } : previous));
  };

  const handleFieldEditorSave = () => {
    if (!fieldEditor) {
      return;
    }
    handleRowFieldChange(
      fieldEditor.rowKey,
      fieldEditor.field,
      fieldEditor.value
    );
    setFieldEditor(null);
  };

  const handleFieldEditorCancel = () => {
    setFieldEditor(null);
  };

  const handleBooleanToggle = (
    rowKey: string,
    field: keyof Pick<SourceEntityColumnMapping, "business_key" | "pii" | "hash_key">,
    currentValue: string | boolean | null | undefined
  ) => {
    const targetRow = columnRows.find((row) => buildRowKey(row) === rowKey);
    if (isMetadataLockedRowKey(rowKey)) {
      return;
    }
    if (
      (selectedEntity?.type === "satellite" || selectedEntity?.type === "link") &&
      targetRow &&
      (targetRow.is_inherited || targetRow.is_locked)
    ) {
      return;
    }
    const nextValue = isTrueFlag(currentValue) ? "N" : "Y";
    // A satellite or link takes its business keys from the hub (the seeds). From its own
    // rows a key can only be taken OFF — the escape hatch for one the hub has dropped.
    if (
      field === "business_key" &&
      nextValue === "Y" &&
      (selectedEntity?.type === "satellite" || selectedEntity?.type === "link")
    ) {
      return;
    }
    if (field === "hash_key") {
      setColumnRows((previous) =>
        previous.map((row) => {
          const isTargetRow = buildRowKey(row) === rowKey;
          if (isTargetRow) {
            return {
              ...row,
              hash_key: nextValue,
              business_key: nextValue === "Y" ? false : row.business_key,
              business_key_order: nextValue === "Y" ? null : row.business_key_order
            };
          }
          return nextValue === "Y" ? { ...row, hash_key: false } : row;
        })
      );
      setSelectedRowIds((previous) => {
        const next = new Set(previous);
        if (nextValue === "Y") {
          next.add(rowKey);
        }
        return next;
      });
      return;
    }
    handleRowFieldChange(rowKey, field, nextValue);
    if (field === "business_key" && nextValue === "N") {
      handleRowFieldChange(rowKey, "business_key_for_target_entity_rdv_id", null);
      handleRowFieldChange(rowKey, "business_key_for_target_entity_rdv_name", null);
      handleRowFieldChange(rowKey, "business_key_order", null);
    }
    if (field === "business_key" && nextValue === "Y") {
      handleRowFieldChange(rowKey, "hash_key", "N");
    }
  };

  /**
   * Add a column that has no source: its value IS the transform.
   *
   * SGDS's BKCC is the canonical case — the common views stamp the business they serve
   * ('DAHL', 'OPT'), which nothing in the source produces. Before the backend allowed a
   * null source column the only way to model this was to invent a source column named
   * after the value, which is what the legacy migration had to do.
   */
  /**
   * Write out this entity's source columns with whatever they are currently called.
   *
   * This is what makes the import usable: it hands the configurator a file already containing
   * every source column name against the name it would actually get. Editing that column and
   * importing it back is the whole workflow, and the file documents its own format — the same
   * pairing the source-entity column manager uses.
   *
   * The target side is the EFFECTIVE name, not only the mapped one. Where nothing is mapped the
   * default applies, and the default is the source column name — that is what the grid shows and
   * what `handleSave` falls back to. Exporting a blank there would hide the answer the file is
   * supposed to give; exporting the default means the file reads as "here is what you get", and
   * the configurator only edits the rows they want called something else.
   */
  const handleExportTargetNames = () => {
    const header = ["source_entity_column_name", "target_entity_rdv_column_name"];
    // A key column is edited through its seed, so the seed holds its current name.
    const seedNameByColumnId = new Map<number, string | null>();
    inheritedColumnRows.forEach((seed) => {
      const columnId = parseNumericOrNull(seed.target_entity_rdv_column_id);
      if (columnId != null) {
        seedNameByColumnId.set(columnId, seed.target_entity_rdv_column_name);
      }
    });
    const lines = [
      header.join(","),
      ...columnRows
        .filter((row) => (row.source_entity_column_name ?? "").trim().length > 0)
        .map((row) =>
          [
            row.source_entity_column_name ?? "",
            // falls back to the source name exactly as the grid and the save path do
            (
              (isSeedClaimed(row)
                ? seedNameByColumnId.get(
                    parseNumericOrNull(row.target_entity_rdv_column_id) ?? -1
                  )
                : null) ??
              row.target_entity_rdv_column_name ??
              row.source_entity_column_name ??
              ""
            ).trim()
          ]
            .map(csvEscape)
            .join(",")
        )
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchorEl = document.createElement("a");
    anchorEl.href = url;
    anchorEl.download = `${targetName || "entity"}-target-names.csv`;
    anchorEl.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Bring target column names in from a CSV, matched on SOURCE COLUMN NAME.
   *
   * Repointing an entity at a different source leaves every mapping dangling, because mappings key
   * on `source_entity_column_id` and the new entity's ids are different — so the names have to be
   * typed again even where they are identical. This reads them from a file instead.
   *
   * NOTHING IS SAVED. The rows are updated in place, the modal's own pending-state machinery marks
   * them changed, and the configurator presses Save columns.
   */
  const handleImportTargetNames = (file: File | null | undefined) => {
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onerror = () =>
      setTargetNameImportReport({ message: `Could not read ${file.name}.`, unmatched: [] });
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      const parsed = parseCsvRows(text);
      if (parsed.length === 0) {
        setTargetNameImportReport({ message: `${file.name} has no rows.`, unmatched: [] });
        return;
      }
      const header = readCsvHeader(parsed[0], [
        "source_entity_column_name",
        "source_column",
        "source"
      ]);
      const pairs = readTargetNamePairs(parsed, header);
      if (pairs.length === 0) {
        setTargetNameImportReport({
          message: `${file.name} has no source column names in it.`,
          unmatched: []
        });
        return;
      }
      const outcome = applyTargetNames(columnRows, pairs);
      setColumnRows(outcome.rows);
      // A key column is saved through its seed, not its own row — carry the imported name
      // over, or it would be dropped on save without a word.
      const appliedSources = new Set(outcome.applied.map((name) => name.trim().toLowerCase()));
      const importedSeedNames = new Map<number, string>();
      outcome.rows.forEach((row) => {
        const columnId = parseNumericOrNull(row.target_entity_rdv_column_id);
        const source = (row.source_entity_column_name ?? "").trim().toLowerCase();
        if (columnId != null && isSeedClaimed(row) && appliedSources.has(source)) {
          importedSeedNames.set(columnId, row.target_entity_rdv_column_name ?? "");
        }
      });
      if (importedSeedNames.size) {
        setInheritedColumnRows((previous) =>
          previous.map((seed) => {
            const columnId = parseNumericOrNull(seed.target_entity_rdv_column_id);
            return columnId != null && importedSeedNames.has(columnId)
              ? { ...seed, target_entity_rdv_column_name: importedSeedNames.get(columnId) ?? "" }
              : seed;
          })
        );
      }

      // Naming a column has to INCLUDE it as well. Only rows in `selectedRowIds` are persisted
      // (see `rowsToPersist`), and rows outside it are actively DELETED on save — so importing a
      // name without modelling the row would be inert at best. A file saying "call this column X"
      // plainly means the column is wanted.
      const namedSourceColumns = new Set(
        [...outcome.applied, ...outcome.unchanged].map((name) => name.trim().toLowerCase())
      );
      if (namedSourceColumns.size) {
        setSelectedRowIds((previous) => {
          const next = new Set(previous);
          outcome.rows.forEach((row) => {
            const name = (row.source_entity_column_name ?? "").trim().toLowerCase();
            if (name && namedSourceColumns.has(name) && canToggleModeledState(row)) {
              next.add(buildRowKey(row));
            }
          });
          return next;
        });
      }

      setTargetNameImportReport({
        message: describeTargetNameImport(outcome),
        unmatched: outcome.unmatched
      });
    };
    reader.readAsText(file);
    // Clear the input so re-choosing the SAME file still fires a change event.
    if (targetNameFileRef.current) {
      targetNameFileRef.current.value = "";
    }
  };

  const handleAddConstantColumn = () => {
    const newRow = {
      source_target_entity_rdv_column_mapping_id: null,
      source_entity_column_id: null,
      source_entity_column_name: null,
      source_entity_id: selectedEntity?.entity.record.source_entity_id
        ? Number(selectedEntity.entity.record.source_entity_id)
        : null,
      source_entity_name: null,
      target_entity_rdv_id: selectedEntity?.entity.record.target_entity_rdv_id
        ? Number(selectedEntity.entity.record.target_entity_rdv_id)
        : null,
      target_entity_rdv_name: null,
      target_schema: null,
      target_entity_rdv_column_id: null,
      target_entity_rdv_column_name: "",
      mapped_target_entity_rdv_column_name: null,
      business_key: false,
      business_key_order: null,
      business_key_for: null,
      business_key_for_target_entity_rdv_id: null,
      hash_key: false,
      pii: false,
      description: null,
      target_entity_rdv_column_transform: "",
      is_constant: true,
      client_row_key: `constant-${nextConstantRowId.current++}`
    } as SourceEntityColumnMapping;
    setColumnRows((previous) => [newRow, ...previous]);
    // A new constant is only useful once it is modelled, so include it straight away.
    setSelectedRowIds((previous) => {
      const next = new Set(previous);
      next.add(buildRowKey(newRow));
      return next;
    });
  };

  const handleHubHashKeyNameChange = (value: string) => {
    setHubHashKeyName(value);
  };

  const moveHubBusinessKeyRow = (rowKey: string, direction: -1 | 1) => {
    setHubBusinessKeyOrder((previous) => {
      const currentIndex = previous.indexOf(rowKey);
      if (currentIndex === -1) {
        return previous;
      }
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= previous.length) {
        return previous;
      }
      const next = [...previous];
      const [item] = next.splice(currentIndex, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
  };

  const handleSaveColumns = async () => {
    if (!selectedEntity) {
      return;
    }
    // A link has no rows of its own in this grid — its editable rows are the canonical keys
    // inherited from its hubs. Saving those is the only way to map a link's business keys,
    // so allow it whenever at least one seed carries a source column.
    const linkSeedsReady =
      isLinkEntity &&
      inheritedColumnRows.some(
        (row) =>
          row.is_canonical_key && (row.source_entity_column_name ?? "").trim().length > 0
      );
    // Stale link keys the modeller switched off. Only the key changes — the row is not
    // otherwise editable here, so its other fields go back as stored — and the backend drops
    // the order along with the key.
    const demotedLinkKeys = staleLinkKeyRows.filter(
      (row) => !isBusinessKeyTrue(row.business_key)
    );
    if (isLinkEntity && !linkSeedsReady && !demotedLinkKeys.length) {
      setSaveErrorMessage(
        "Pick a source column for at least one inherited business key before saving."
      );
      setSaveSuccessMessage(null);
      return;
    }
    if (!isLinkEntity && !columnRows.length) {
      setSaveErrorMessage("Add at least one column mapping before saving.");
      setSaveSuccessMessage(null);
      return;
    }
    // A visible seed, or a constant, owns its target column name. If the source table also
    // has a column of that name — customer_v really does have a physical BKCC beside the
    // constant — modelling it as a plain attribute too would send the same target column
    // twice: once as the key/constant and once as a non-key duplicate.
    const seedOwnedTargets = new Set(
      [
        ...inheritedColumnRows,
        ...columnRows.filter((row) => Boolean(row.is_constant))
      ]
        .map((row) => (row.target_entity_rdv_column_name ?? "").trim().toLowerCase())
        .filter(Boolean)
    );
    const rowsToPersist = columnRows.filter(
      (row) =>
        selectedRowIds.has(buildRowKey(row)) &&
        // the seed writes this column instead — see displayedRows
        !isSeedClaimed(row) &&
        !(
          !row.is_constant &&
          (row.mapped_target_entity_rdv_column_name ?? "").trim().length === 0 &&
          seedOwnedTargets.has((row.target_entity_rdv_column_name ?? "").trim().toLowerCase())
        )
    );
    const environmentId =
      typeof window !== "undefined"
        ? getScopeEnvironmentId() ?? FALLBACK_ENVIRONMENT_ID
        : FALLBACK_ENVIRONMENT_ID;
    const customerId =
      typeof window !== "undefined"
        ? getScopeCustomerId() ?? FALLBACK_CUSTOMER_ID
        : FALLBACK_CUSTOMER_ID;
    if (!environmentId || !customerId) {
      setSaveErrorMessage("Missing environment or customer context.");
      setSaveSuccessMessage(null);
      return;
    }
    const sourceEntityId = selectedEntity.entity.record.source_entity_id
      ? Number(selectedEntity.entity.record.source_entity_id)
      : null;
    const targetEntityRdvId = selectedEntity.entity.record.target_entity_rdv_id
      ? Number(selectedEntity.entity.record.target_entity_rdv_id)
      : null;
    if (!sourceEntityId || !targetEntityRdvId) {
      setSaveErrorMessage("Missing source or target references for this entity.");
      setSaveSuccessMessage(null);
      return;
    }
    if (requireLinkBusinessKeySelection) {
      const connectedCoverage = new Set<number>();
      // A link never has rows of its own here — setColumnRows stores [] for links, and its
      // editable rows are the canonical key seeds. Reading columnRows made this guard
      // impossible to satisfy, so every multi-hub link (93% of them) refused to save even
      // with every key mapped.
      // `already_mapped` means the LINK ALREADY STORES that key, which is coverage on its
      // own — the seed's prefilled source column is only a suggestion. Reading the prefill
      // alone refused links whose stored key simply has a different name from the hub's
      // (env7 `item__negotiation_category_l` stores `negotiation_category` for the hub's
      // `negotiation_category_code`), so a complete link could never be saved.
      const coverageRows = isLinkEntity
        ? inheritedColumnRows.filter(
            (row) =>
              row.is_canonical_key &&
              (isTrueFlag(row.already_mapped) ||
                (row.source_entity_column_name ?? "").trim().length > 0)
          )
        : columnRows;
      coverageRows.forEach((row) => {
        if (!isBusinessKeyTrue(row.business_key)) {
          return;
        }
        // An unmapped seed carries only the hub it came from, so fall back to that.
        const hubId =
          parseNumericOrNull(row.business_key_for_target_entity_rdv_id) ??
          parseNumericOrNull(row.inherited_from_target_entity_rdv_id);
        if (hubId != null) {
          connectedCoverage.add(hubId);
        }
      });
      const missingHubs = linkBusinessKeyOptions.filter(
        (option) => !connectedCoverage.has(option.id)
      );
      if (missingHubs.length) {
        const missingList = missingHubs.map((hub) => hub.name).join(", ");
        const validationMessage =
          missingHubs.length === 1
            ? `Add a business key mapped to ${missingList} before saving.`
            : `Add business keys mapped to: ${missingList}.`;
        setSaveErrorMessage(validationMessage);
        setSaveSuccessMessage(null);
        return;
      }
    }
    if (isHubEntity) {
      const hashKeyRows = columnRows.filter((row) => isHashKeyTrue(row.hash_key));
      if (hashKeyRows.length > 1) {
        setSaveErrorMessage("Select exactly one hash key output row for this hub.");
        setSaveSuccessMessage(null);
        return;
      }
      const actualOrders = hubBusinessKeyRows.map((row, index) => {
        const rowKey = buildRowKey(row);
        return hubBusinessKeyIndexByRowKey.get(rowKey) ?? index + 1;
      });
      const expectedOrders = actualOrders.map((_, index) => index + 1);
      if (actualOrders.some((order, index) => order !== expectedOrders[index])) {
        setSaveErrorMessage("Business key component order must be contiguous starting at 1.");
        setSaveSuccessMessage(null);
        return;
      }
    }
    const sanitizeId = (value: number | string | null | undefined) => {
      const parsed = parseNumericOrNull(value);
      return parsed && parsed > 0 ? parsed : null;
    };
    const deleteRows: DeleteColumnMappingPayload[] = columnRows
      .filter((row) => !selectedRowIds.has(buildRowKey(row)))
      .filter((row) => !Boolean(row.is_inherited || row.is_locked))
      // its seed is writing this column in the same save
      .filter((row) => !isSeedClaimed(row))
      .map((row) => ({
        source_target_entity_rdv_column_mapping_id: sanitizeId(
          row.source_target_entity_rdv_column_mapping_id
        ),
        target_entity_rdv_column_id: sanitizeId(row.target_entity_rdv_column_id)
      }))
      .filter(
        (row) =>
          parseNumericOrNull(row.source_target_entity_rdv_column_mapping_id) != null ||
          parseNumericOrNull(row.target_entity_rdv_column_id) != null
      );
    const payloadRows: SaveColumnMappingPayload[] = rowsToPersist
      .map((row): SaveColumnMappingPayload | null => {
        const isRowHashKey = isHashKeyTrue(row.hash_key);
        const sourceColumnName = row.source_entity_column_name?.trim() ?? "";
        const targetColumnName =
          row.target_entity_rdv_column_name?.trim() ||
          row.source_entity_column_name?.trim() ||
          "";
        if (!targetColumnName) {
          return null;
        }
        const isRowBusinessKey = isBusinessKeyTrue(row.business_key);
        if (isRowHashKey) {
          return {
            target_entity_rdv_id: row.target_entity_rdv_id ?? targetEntityRdvId,
            target_entity_rdv_column_id: sanitizeId(row.target_entity_rdv_column_id),
            target_entity_rdv_column_name: targetColumnName || null,
            business_key: false,
            business_key_order: null,
            business_key_for: null,
            hash_key: true,
            pii: false,
            description: row.description?.trim() || null
          };
        }
        // A constant column has no source column on purpose — its transform holds the
        // value — so the "pick a source first" rule does not apply to it.
        const rowIsConstant = Boolean(row.is_constant);
        if (!sourceColumnName && !rowIsConstant) {
          return null;
        }
        // An "Add custom column" row the modeller never filled in. Dropping it silently
        // is right: they added it and changed their mind. A HALF-filled one still goes to
        // the backend, which explains exactly what is missing.
        if (
          rowIsConstant &&
          !targetColumnName &&
          !(row.target_entity_rdv_column_transform ?? "").trim()
        ) {
          return null;
        }
        return {
          source_target_entity_rdv_column_mapping_id: sanitizeId(
            row.source_target_entity_rdv_column_mapping_id
          ),
          is_constant: rowIsConstant,
          source_entity_column_id: rowIsConstant
            ? null
            : sanitizeId(row.source_entity_column_id),
          source_entity_id: row.source_entity_id ?? sourceEntityId,
          source_entity_column_name: rowIsConstant ? null : sourceColumnName || null,
          target_entity_rdv_id: row.target_entity_rdv_id ?? targetEntityRdvId,
          target_entity_rdv_column_id: sanitizeId(row.target_entity_rdv_column_id),
          target_entity_rdv_column_name: targetColumnName || null,
          business_key: isRowBusinessKey,
          business_key_order: isRowBusinessKey
            ? hubBusinessKeyIndexByRowKey.get(buildRowKey(row)) ??
              parseNumericOrNull(row.business_key_order)
            : null,
          business_key_for: isRowBusinessKey
            ? sanitizeId(row.business_key_for_target_entity_rdv_id)
            : null,
          hash_key: false,
          pii: isTrueFlag(row.pii),
          description: row.description?.trim() || null,
          target_entity_rdv_column_transform: row.target_entity_rdv_column_transform?.trim() || null
        };
      })
      .filter((row): row is SaveColumnMappingPayload => Boolean(row));
    // Canonical keys inherited from the hub are persisted as this entity's OWN columns:
    // the hub's target name and order (that is what keeps the hashes equal) with the
    // source column and transform chosen here. Unmapped seeds are simply skipped, so a
    // modeller can save partial progress.
    const seedRows: SaveColumnMappingPayload[] = inheritedColumnRows
      .filter((row) => row.is_canonical_key)
      // A seed is ready when it has a source column, OR when it is a constant carrying a
      // value. Anything else is a key the modeller has not answered yet — skip it, so
      // modelling four keys can still take four steps.
      .filter((row) =>
        row.is_constant
          ? (row.target_entity_rdv_column_transform ?? "").trim().length > 0
          : (row.source_entity_column_name ?? "").trim().length > 0
      )
      .map((row) => ({
        is_constant: Boolean(row.is_constant),
        source_entity_column_id: row.is_constant
          ? null
          : sanitizeId(row.source_entity_column_id),
        source_entity_id: row.source_entity_id ?? sourceEntityId,
        source_entity_column_name: row.is_constant
          ? null
          : row.source_entity_column_name?.trim() || null,
        target_entity_rdv_id: row.target_entity_rdv_id ?? targetEntityRdvId,
        source_target_entity_rdv_column_mapping_id: sanitizeId(
          row.source_target_entity_rdv_column_mapping_id
        ),
        target_entity_rdv_column_id: sanitizeId(row.target_entity_rdv_column_id),
        target_entity_rdv_column_name: row.target_entity_rdv_column_name?.trim() || null,
        business_key: true,
        business_key_order: parseNumericOrNull(row.business_key_order),
        business_key_for: sanitizeId(
          row.business_key_for_target_entity_rdv_id ??
            row.inherited_from_target_entity_rdv_id
        ),
        // Which hub key this is a copy of. Recorded so the two stay identifiable even if
        // either side is renamed — the modeller is free to name their copy differently.
        business_key_for_target_entity_rdv_column_id: sanitizeId(
          row.business_key_for_target_entity_rdv_column_id
        ),
        hash_key: false,
        // Do not force the hub's description or a pii default onto the child's column —
        // the backend applies these unconditionally when present. A seed that takes over a
        // stored column (a key it has, or a plain column being promoted back) carries that
        // column's own values, so send them; sending null for a promoted column wiped its
        // description. A brand-new column has nothing of its own yet.
        pii: isTrueFlag(row.pii),
        description:
          sanitizeId(row.target_entity_rdv_column_id) != null
            ? row.description?.trim() || null
            : null,
        target_entity_rdv_column_transform:
          row.target_entity_rdv_column_transform?.trim() || null
      }));
    payloadRows.push(...seedRows);
    payloadRows.push(
      ...demotedLinkKeys.map((row) => ({
        source_target_entity_rdv_column_mapping_id: sanitizeId(
          row.source_target_entity_rdv_column_mapping_id
        ),
        target_entity_rdv_id: row.target_entity_rdv_id ?? targetEntityRdvId,
        target_entity_rdv_column_id: sanitizeId(row.target_entity_rdv_column_id),
        target_entity_rdv_column_name: row.target_entity_rdv_column_name?.trim() || null,
        business_key: false,
        business_key_order: null,
        business_key_for: null,
        hash_key: false,
        pii: isTrueFlag(row.pii),
        description: row.description?.trim() || null
      }))
    );
    if (isHubEntity) {
      const modeledRows = columnRows.filter((row) => selectedRowIds.has(buildRowKey(row)));
      const hasBusinessKeys = modeledRows.some((row) => isBusinessKeyTrue(row.business_key));
      const existingHashKeyRows = modeledRows.filter((row) => isHashKeyTrue(row.hash_key));
      const hashKeyAlreadyInPayload = payloadRows.some((row) => row.hash_key);
      if (hasBusinessKeys && existingHashKeyRows.length === 0 && !hashKeyAlreadyInPayload) {
        payloadRows.push({
          target_entity_rdv_id: targetEntityRdvId,
          target_entity_rdv_column_id: null,
          target_entity_rdv_column_name: hubHashKeyName.trim() || `${targetName || "hub"}_hash`,
          business_key: false,
          business_key_order: null,
          business_key_for: null,
          hash_key: true,
          pii: false,
          description: null
        });
      }
    }
    if (!payloadRows.length && !deleteRows.length) {
      setSaveErrorMessage("Make at least one modeling change before saving.");
      setSaveSuccessMessage(null);
      return;
    }
    setIsSavingColumns(true);
    setSaveErrorMessage(null);
    setSaveSuccessMessage(null);
    try {
      const rowsToUpdate = payloadRows.filter((row) => {
        const hasMappingId = parseNumericOrNull(row.source_target_entity_rdv_column_mapping_id) != null;
        const hasTargetColumnId = parseNumericOrNull(row.target_entity_rdv_column_id) != null;
        return hasMappingId || hasTargetColumnId;
      });
      const rowsToCreate = payloadRows.filter((row) => {
        const hasMappingId = parseNumericOrNull(row.source_target_entity_rdv_column_mapping_id) != null;
        const hasTargetColumnId = parseNumericOrNull(row.target_entity_rdv_column_id) != null;
        return !hasMappingId && !hasTargetColumnId;
      });
      // One request, one transaction, judged once at the end.
      //
      // These used to be three calls, creates first — a hub's synthesised hash column is
      // always a create, and running the updates first left the hub with business keys and
      // no hash column, which the backend rejected for every migrated hub even with no
      // edits. But each call also validated the half of the save it had applied, so an edit
      // that moved one hub key while adding another was judged halfway through: the new key
      // landed on position 4 while the key being moved off 4 was still there, in the update
      // call that had not run yet. env7 customer_sales_order_line_h, "duplicate
      // business_key_order values", for a payload whose final state was valid and which no
      // UI control could clear.
      //
      // The order below is still creates, updates, deletes — the backend applies them in
      // that order for the same hash-column reason. What changed is that nothing observes
      // the state between them.
      //
      // The save succeeds even when the model is not yet coherent — you cannot update a
      // hub's satellites before the hub has the key. The backend says what still does not
      // line up, and that has to reach the modeller: adding a key to a hub stops every
      // satellite under it from joining until each one answers the key too.
      const collectedWarnings: string[] = [];
      const collectWarnings = (response: unknown) => {
        const list = (response as { warnings?: unknown })?.warnings;
        if (Array.isArray(list)) {
          collectedWarnings.push(...list.filter((entry): entry is string => typeof entry === "string"));
        }
      };
      collectWarnings(
        await saveSourceEntityColumnMappings(
          { create: rowsToCreate, update: rowsToUpdate, delete: deleteRows },
          environmentId,
          customerId
        )
      );
      setSaveWarnings(Array.from(new Set(collectedWarnings)));
      setSaveSuccessMessage("Column mappings saved successfully.");
      setReloadColumnsKey((value) => value + 1);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to save column mappings at this time.";
      setSaveErrorMessage(message);
    } finally {
      setIsSavingColumns(false);
    }
  };

  const targetName =
    selectedEntity.entity.record.target_entity_rdv_name ??
    selectedEntity.entity.record.target_table ??
    "";
  const entityLabel =
    selectedEntity.type === "link"
      ? "Link"
      : selectedEntity.type === "satellite"
        ? "Satellite"
        : "Hub";
  const formattedTarget = formatLabel(targetName);
  const title = `Column Mappings - ${formattedTarget || entityLabel}`.trim();
  const totalTableColumns =
    7 + (showBusinessKeyForColumn && !isLinkEntity ? 1 : 0) + (isHubEntity ? 1 : 0);
  const inheritedRowLabel = isLinkEntity
    ? "Inherited from connected hub"
    : "Inherited from parent hub";
  // Which hub a key serves is the only thing telling two same-named rows apart — a link
  // inheriting BKCC from both of its hubs shows it twice, once per hash it feeds.
  const inheritedRowLabelFor = (row: SourceEntityColumnMapping) =>
    row.inherited_from_target_entity_rdv_name
      ? `Inherited from ${row.inherited_from_target_entity_rdv_name}`
      : inheritedRowLabel;

  return (
    <ColumnMappingModalShell
      title={title}
      contextLabel="Source entity"
      contextChips={sourceEntityChips}
      toolbar={
        isLinkEntity ? null : (
          <>
            <button
              type="button"
              onClick={() => handleBulkModeledUpdate(true)}
              className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-border-slate-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-slate-300"
            >
              Include visible
            </button>
            <button
              type="button"
              onClick={() => handleBulkModeledUpdate(false)}
              className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-border-slate-400 focus:tw-outline-none focus:tw:ring-1 focus:tw:ring-slate-300"
            >
              Exclude visible
            </button>
            <button
              type="button"
              onClick={handleAddConstantColumn}
              title="A column with no source: its value is whatever the transform emits, e.g. 'DAHL'."
              className="tw-rounded-full tw-border tw-border-indigo-200 tw-bg-indigo-50 tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-indigo-700 hover:tw-border-indigo-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-indigo-300"
            >
              Add custom column
            </button>
            <input
              ref={targetNameFileRef}
              type="file"
              accept=".csv,text/csv"
              className="tw-hidden"
              onChange={(event) => handleImportTargetNames(event.target.files?.[0])}
            />
            {/* Export sits BEFORE import on purpose: it is the step that tells you what the file
                should contain, by handing you one with every source column already in it. */}
            <button
              type="button"
              onClick={handleExportTargetNames}
              disabled={columnLoading || columnRows.length === 0}
              title="Download this entity's source columns with their current target names, ready to fill in and import back."
              className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-border-slate-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-slate-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            >
              Export target names
            </button>
            <button
              type="button"
              onClick={() => targetNameFileRef.current?.click()}
              // This toolbar renders before the source columns have arrived. Without the guard the
              // control is live against an empty grid, and every import reports "0 columns named"
              // for no reason the user can see.
              disabled={columnLoading || columnRows.length === 0}
              title={
                columnLoading || columnRows.length === 0
                  ? "Available once the source columns have loaded."
                  : "CSV of source column name, target column name. Matched on the source name; nothing is written until you press Save columns."
              }
              className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-border-slate-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-slate-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            >
              Import target names
            </button>
            <button
              type="button"
              aria-pressed={showOnlySelectedRows}
              disabled={!selectedCount}
              onClick={() => setShowOnlySelectedRows((previous) => !previous)}
              className={`tw-rounded-full tw-border tw-border-slate-200 tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-slate-300 ${showOnlySelectedRows
                ? "tw-bg-[#03234a] tw-text-white hover:tw-border-slate-500"
                : "tw-bg-white tw-text-slate-700 hover:tw-border-slate-400"
                } disabled:tw-cursor-not-allowed disabled:tw-opacity-50`}
            >
              {showOnlySelectedRows ? "Showing modeled" : "Show modeled only"}
            </button>
            <div className="tw-rounded-full tw-bg-slate-100 tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600">
              {selectedCount} of {columnRows.length} {columnRows.length === 1 ? "row" : "rows"} modeled
            </div>
            {hashColumnBudget ? (
              // A satellite's change-detection hash is ONE CONCAT_WS over its non-key columns, and
              // a T-SQL target caps that at 253 values. The server rejects an over-wide statement
              // when the pipeline RUNS, so without this the first sign is a failed customer load.
              // Shown only past the threshold, and only on targets that carry the limit.
              <div
                className={`tw-rounded-full tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold ${
                  hashColumnBudget.exceeded
                    ? "tw-bg-rose-100 tw-text-rose-800"
                    : "tw-bg-amber-100 tw-text-amber-800"
                }`}
                title={hashColumnBudget.message}
              >
                {hashColumnBudget.exceeded ? "Too many hash columns: " : "Hash columns: "}
                {hashColumnBudget.used}/{hashColumnBudget.limit}
              </div>
            ) : null}
            {pendingCount > 0 ? (
              // The count belongs where the eye already is. Without it, unsaved work is
              // only discoverable by scanning every row.
              <div
                className="tw-rounded-full tw-bg-amber-100 tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-amber-800"
                title="Nothing is written until you press Save columns."
              >
                {describePending(pendingCount)}
              </div>
            ) : null}
            {!targetNameImportReport && !columnLoading && columnRows.length > 0 ? (
              // A bare pair of buttons tells a first-time user nothing about what the file should
              // contain. This says the whole workflow in one line, and gets out of the way as soon
              // as an import has actually run.
              <span
                className="tw-text-[11px] tw-font-medium tw-text-slate-500"
                title="The exported file has one row per source column: source_entity_column_name, target_entity_rdv_column_name, pre-filled with the name each column would get. Edit the second column and import it back."
              >
                Renaming a lot of columns? Export → edit the target names → Import
              </span>
            ) : null}
            {targetNameImportReport ? (
              // Says what the file did. An import that silently matched nothing is the failure
              // mode worth guarding against, so the counts are always shown, not only on error.
              <div
                className="tw-rounded-full tw-bg-slate-100 tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-slate-700"
                title={
                  targetNameImportReport.unmatched.length
                    ? `Not on this source: ${targetNameImportReport.unmatched.join(", ")}`
                    : undefined
                }
              >
                {targetNameImportReport.message}
                <button
                  type="button"
                  onClick={() => setTargetNameImportReport(null)}
                  className="tw-ml-2 tw-text-slate-400 hover:tw-text-slate-700"
                  aria-label="Dismiss import result"
                >
                  ×
                </button>
              </div>
            ) : null}
            <select
              value={rowViewFilter}
              onChange={(event) =>
                setRowViewFilter(
                  event.target.value as "all" | "mapped" | "unmapped" | "inherited"
                )
              }
              className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-700 focus:tw-outline-none focus:tw:ring-1 focus:tw:ring-slate-300"
            >
              <option value="all">All rows</option>
              <option value="mapped">Modeled</option>
              <option value="unmapped">Available</option>
              <option value="inherited">Inherited</option>
            </select>
            <input
              type="text"
              value={rowSearchQuery}
              onChange={(event) => setRowSearchQuery(event.target.value)}
              placeholder="Search columns..."
              className="tw-w-52 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 focus:tw-outline-none focus:tw:ring-1 focus:tw:ring-slate-300"
            />
          </>
        )
      }
      actionCard={
        <ColumnMappingActionCard
          saveBehaviorText={
            isLinkEntity
              ? // Links DO save from here now — this is the only place a link's business keys
                // can be mapped. The old wording described the behaviour before that.
                "A link's business keys come from its connected hubs. Give each one a source column here, or a custom value, and save."
              : "Available rows can be included. Modeled rows can be updated or removed when saving."
          }
          onClose={() => {
            // Closing silently discards pending work — the whole point of this pass is that
            // "proposed" and "stored" are different, so leaving must say so.
            if (
              pendingCount > 0 &&
              !window.confirm(
                `${describePending(pendingCount)} on this entity. Close without saving?`
              )
            ) {
              return;
            }
            onClose();
          }}
          onSave={handleSaveColumns}
          saveLabel={pendingCount > 0 ? `Save columns (${pendingCount})` : "Save columns"}
          isSaving={isSavingColumns}
          disabled={
            isSavingColumns ||
            (isLinkEntity
              ? !inheritedColumnRows.length && !staleLinkKeyRows.length
              : !columnRows.length)
          }
          errorMessage={saveErrorMessage}
          successMessage={saveSuccessMessage}
          warnings={saveWarnings}
        />
      }
    >
      <>
        {columnLoading ? (
          <p className="tw-text-sm tw-text-slate-500">Loading source columns…</p>
        ) : columnError ? (
          columns.length ? (
            <ColumnCardList columns={columns} />
          ) : (
            <p className="tw-text-sm tw-text-rose-500">{columnError}</p>
          )
        ) : hasDisplayRows ? (
          <>
            {isHubEntity ? (
              <div className="tw-mb-4 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4 tw-shadow-sm">
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                  <div className="tw-space-y-1">
                    <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-slate-500">
                      Business Key Definition
                    </p>
                    <h3 className="tw-text-sm tw-font-semibold tw-text-slate-900">
                      Hub hash key
                    </h3>
                    <p className="tw-text-xs tw-text-slate-500">
                      The hash key is derived from the ordered business key components below.
                    </p>
                  </div>
                  <div className="tw-min-w-[16rem] tw-max-w-[18rem] tw-flex-1">
                    <label className="tw-mb-1 tw-block tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.22em] tw-text-slate-500">
                      Hash key column
                    </label>
                    <input
                      value={hubHashKeyName}
                      onChange={(event) => handleHubHashKeyNameChange(event.target.value)}
                      className={tableInputClass}
                      placeholder={`${targetName || "hub"}_hash`}
                    />
                    <p className="tw-mt-1 tw-text-[11px] tw-text-slate-500">
                      Default: {`${targetName || "hub"}_hash`}
                    </p>
                  </div>
                </div>
                <div className="tw-mt-4">
                  <p className="tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.22em] tw-text-slate-500">
                    Hash key output
                  </p>
                  <div className="tw-mb-4 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-3">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span className="tw-rounded-full tw-bg-emerald-100 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-emerald-700">
                        Derived output
                      </span>
                      <span className="tw-text-xs tw-font-semibold tw-text-slate-900">
                        {hubHashKeyRows[0]?.target_entity_rdv_column_name || hubHashKeyName || `${targetName || "hub"}_hash`}
                      </span>
                    </div>
                    <p className="tw-mt-1 tw-text-[11px] tw-text-slate-500">
                      This is the persisted technical hash column generated from the ordered business key components.
                    </p>
                  </div>
                  <p className="tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.22em] tw-text-slate-500">
                    Business key components
                  </p>
                  {hubBusinessKeyRows.length ? (
                    <div className="tw-space-y-2">
                      {hubBusinessKeyRows.map((row, index) => {
                        const rowKey = buildRowKey(row);
                        const isFirst = index === 0;
                        const isLast = index === hubBusinessKeyRows.length - 1;
                        const transformPreview = row.target_entity_rdv_column_transform?.trim();
                        return (
                          <div
                            key={rowKey}
                            className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-3"
                          >
                            <div className="tw-min-w-0">
                              <div className="tw-flex tw-items-center tw-gap-2">
                                <span className="tw-rounded-full tw-bg-[#03234a] tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white">
                                  #{index + 1}
                                </span>
                                <span className="tw-text-xs tw-font-semibold tw-text-slate-900">
                                  {row.target_entity_rdv_column_name || row.source_entity_column_name || "Unnamed column"}
                                </span>
                              </div>
                              <p className="tw-mt-1 tw-text-[11px] tw-text-slate-500">
                                Source: {row.source_entity_column_name || "—"}
                              </p>
                              {transformPreview ? (
                                <div className="tw-mt-1">
                                  <SqlPreview value={transformPreview} placeholder="" />
                                </div>
                              ) : null}
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <button
                                type="button"
                                onClick={() => moveHubBusinessKeyRow(rowKey, -1)}
                                disabled={isFirst}
                                className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
                              >
                                Up
                              </button>
                              <button
                                type="button"
                                onClick={() => moveHubBusinessKeyRow(rowKey, 1)}
                                disabled={isLast}
                                className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
                              >
                                Down
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="tw-rounded-xl tw-border tw-border-dashed tw-border-slate-200 tw-bg-white tw-p-4 tw-text-sm tw-text-slate-500">
                      Select one or more business key rows to generate the hub hash key.
                    </div>
                  )}
                </div>
              </div>
            ) : null}
            <div className="tw-max-h-[64vh] tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200 tw-shadow-sm">
              <table className="tw-min-w-full tw-table-fixed tw-text-xs tw-text-slate-700 tw-text-center">
                <thead className="tw-sticky tw-top-0 tw-z-10 tw-bg-slate-50 tw-text-center">
                  <tr>
                    <th className="tw-w-32 tw-px-3 tw-py-2">
                      {isLinkEntity ? null : (
                        <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
                          State
                        </span>
                      )}
                    </th>
                    <th className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold">Source Column</th>
                    <th className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold">Target Column</th>
                    <th className="tw-w-24 tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-center tw-font-semibold">Business Key</th>
                    {isHubEntity ? (
                      <th className="tw-w-24 tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-center tw-font-semibold">Hash Key</th>
                    ) : null}
                    {showBusinessKeyForColumn && !isLinkEntity ? (
                      <th className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold">Business Key For</th>
                    ) : null}
                    <th className="tw-w-20 tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-center tw-font-semibold">PII</th>
                    <th className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold">Description</th>
                    <th className="tw-whitespace-nowrap tw-px-3 tw-py-2 tw-font-semibold">Transform</th>
                  </tr>
                </thead>
                <tbody className="tw-divide-y tw-divide-slate-100 tw-bg-white">
                  {displayedRows.length ? (
                    displayedRows.map((row) => {
                      const rowKey = buildRowKey(row);
                      const isSelected = selectedRowIds.has(rowKey);
                      const isInheritedBusinessKey = Boolean(
                        (selectedEntity.type === "satellite" || selectedEntity.type === "link") &&
                        // Only the hub-derived rows are inherited/locked — the backend emits those
                        // separately (inherited_business_key_items) tagged is_inherited/is_locked.
                        // A *regular* satellite column that merely carries the business_key flag is
                        // still a normal modelable attribute (the customer wants the BK column's
                        // underlying value in the satellite, not only in the hub hash), so we must
                        // NOT lock it here — that's why we no longer test isBusinessKeyTrue.
                        (row.is_inherited || row.is_locked)
                      );
                      // The hub/satellite templates emit these columns themselves: modelling one
                      // has no effect on the generated SQL, and flagging it as a business key
                      // drags a technical column into the hub hash — or, once it is the only key,
                      // leaves the hash empty. Links are exempt (they may key on md_system_id).
                      const isTemplateOwnedColumn = isTemplateOwnedRow(
                        selectedEntity.type,
                        row.target_entity_rdv_column_name,
                        row.source_entity_column_name,
                        generatorOwnedColumns
                      );
                      const isHashKeyRow = isHashKeyTrue(row.hash_key);
                      // A key the entity stores but no hub key claims: every key a hub still
                      // has is rendered as its seed (which hides the entity's own row for that
                      // column), so a satellite row still flagged as a key is one its hub has
                      // dropped. A link's are collected at load — it renders no own rows.
                      const isStaleLinkKey = isLinkEntity && staleLinkKeyRowKeys.has(rowKey);
                      const isStaleSatelliteKey =
                        selectedEntity.type === "satellite" &&
                        !row.is_canonical_key &&
                        !isInheritedBusinessKey &&
                        !isTemplateOwnedColumn &&
                        !isHashKeyRow &&
                        isBusinessKeyTrue(row.business_key);
                      const isStaleKey = isStaleLinkKey || isStaleSatelliteKey;
                      // A satellite/link never declares its own business key — that's the hub's.
                      // So even for an editable (non-inherited) sat/link row, the BK toggle stays
                      // read-only; the row is still modelable as a regular attribute otherwise.
                      // The one exception is taking a stale key OFF — never putting one on.
                      const businessKeyToggleDisabled =
                        isInheritedBusinessKey ||
                        isTemplateOwnedColumn ||
                        ((selectedEntity.type === "satellite" ||
                          selectedEntity.type === "link") &&
                          !(isStaleKey && isBusinessKeyTrue(row.business_key)));
                      // A seed for a key the entity already stores is that stored column: what
                      // its own row let the modeller edit stays editable here.
                      const seedOwnsStoredColumn =
                        Boolean(row.is_canonical_key) && isTrueFlag(row.already_mapped);
                      const attributeEditLocked =
                        (isInheritedBusinessKey && !seedOwnsStoredColumn) || isStaleLinkKey;
                      const descriptionEditLocked = !isNewCustomMappingRow(row);
                      // The transform of a business-key row on a satellite/link normally shapes the BK
                      // value the sat/link hashes to join back to the hub; if the sat sources the SAME
                      // column as the hub, its transform must match the hub's (edit here = drift =
                      // broken join), so we lock it. EXCEPTION (custom-hub case): when the sat's BK
                      // sources a DIFFERENT raw column than the hub's clean key (e.g. hub customer_id
                      // <- customer_id no-transform, sat customer_id <- OKCUNO), the sat's transform is
                      // exactly what ALIGNS the two hashes — it is sat-specific and must be editable.
                      // So only lock when the sat's BK source matches the hub's for that target column.
                      const satTargetCol = row.target_entity_rdv_column_name
                        ?.trim()
                        .toLowerCase();
                      const satSourceCol = row.source_entity_column_name
                        ?.trim()
                        .toLowerCase() ?? null;
                      const hubSourceCol = satTargetCol
                        ? hubBusinessKeySourceByTargetColumn.get(satTargetCol)
                        : undefined;
                      // sourcesMatch: hub has this BK and its source equals the sat's -> drift risk ->
                      // keep locked. If the hub sources a different column (or we can't resolve the
                      // hub's source), the sat transform is alignment work -> allow editing.
                      const satBkSourceMatchesHub =
                        hubSourceCol !== undefined &&
                        hubSourceCol !== null &&
                        satSourceCol !== null &&
                        hubSourceCol === satSourceCol;
                      const transformLocked =
                        isInheritedBusinessKey ||
                        ((selectedEntity.type === "satellite" ||
                          selectedEntity.type === "link") &&
                          isBusinessKeyTrue(row.business_key) &&
                          satBkSourceMatchesHub);
                      const modeledToggleDisabled = !canToggleModeledState(row);
                      const isMetadataLocked = isMetadataSourceColumn(
                        row.source_entity_column_name
                      );
                      const businessKeyOrder = hubBusinessKeyIndexByRowKey.get(rowKey);
                      return (
                        <tr
                          key={rowKey}
                          // A pending row carries an amber left edge, so unsaved work is
                          // scannable down the grid without reading every chip.
                          className={`tw-border-b tw-border-slate-100 ${
                            pendingByRowKey.has(rowKey)
                              ? "tw-border-l-4 tw-border-l-amber-400 tw-bg-amber-50/40"
                              : isSelected
                              ? "tw-bg-slate-50"
                              : "tw-bg-white"
                          }`}
                        >
                          <td className="tw-whitespace-nowrap tw-px-3 tw-py-3 tw-text-center">
                            {isLinkEntity ? null : pendingByRowKey.get(rowKey) ===
                              "new" ? (
                              // Never stored. Green "Inherited" read as a settled fact and
                              // is how an inherited key got left unsaved.
                              <span
                                className="tw-inline-flex tw-rounded-full tw-bg-amber-100 tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-amber-800"
                                title="Not saved yet — this is a proposal until you save."
                              >
                                {isInheritedBusinessKey ? "Suggested" : "New"}
                              </span>
                            ) : pendingByRowKey.get(rowKey) === "changed" ? (
                              <span
                                className="tw-inline-flex tw-rounded-full tw-bg-amber-100 tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-amber-800"
                                title="Edited since it was loaded — not saved yet."
                              >
                                Unsaved
                              </span>
                            ) : isInheritedBusinessKey ? (
                              <span className="tw-inline-flex tw-rounded-full tw-bg-emerald-50 tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-emerald-700">
                                Inherited
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleRowSelect(row)}
                                disabled={modeledToggleDisabled}
                                className={`tw-inline-flex tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-transition ${
                                  isSelected
                                    ? "tw-bg-slate-100 tw-text-slate-700"
                                    : "tw-border tw-border-slate-300 tw-bg-white tw-text-slate-500"
                                } disabled:tw-cursor-not-allowed disabled:tw-opacity-50`}
                              >
                                {isSelected ? "Modeled" : "Available"}
                              </button>
                            )}
                          </td>
                          <td className="tw-w-48 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
                            {isHashKeyRow ? (
                              <div className="tw-flex tw-flex-col tw-gap-1">
                                <div className="tw-flex tw-h-7 tw-w-full tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-2.5 tw-text-left tw-text-xs tw-text-slate-500">
                                  Derived hash output
                                </div>
                                <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-emerald-700">
                                  Persisted technical column
                                </span>
                              </div>
                            ) : row.is_constant && !row.is_canonical_key ? (
                              <div className="tw-flex tw-flex-col tw-gap-1">
                                {/* No source column by design — the transform holds the
                                    value. Showing a picker here would invite exactly the
                                    fake source column the migration had to invent.
                                    A canonical-key SEED that is a constant is handled by
                                    the seed branch below instead, so it keeps both its hub
                                    attribution and the picker it can be switched back with. */}
                                <div className="tw-flex tw-h-7 tw-w-full tw-items-center tw-rounded-lg tw-border tw-border-indigo-200 tw-bg-indigo-50 tw-px-2.5 tw-text-left tw-text-xs tw-text-indigo-700">
                                  No source column
                                </div>
                                <span
                                  className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-indigo-700"
                                  title="A custom column has no source: its value is whatever the transform emits, for example 'DAHL'."
                                >
                                  Custom value
                                </span>
                              </div>
                            ) : row.is_canonical_key ? (
                              <div className="tw-flex tw-flex-col tw-gap-1">
                                {/* The hub's key, sourced from THIS entity's own column —
                                    the hub's source column usually does not exist here.
                                    It may also have no source column at all: BKCC is a
                                    constant, and each satellite stamps its own literal
                                    ('DAHL' vs 'OPT'), so that has to be answerable here. */}
                                <select
                                  value={
                                    row.is_constant
                                      ? CONSTANT_SOURCE_SENTINEL
                                      : row.source_entity_column_name ?? ""
                                  }
                                  onChange={(event) =>
                                    handleRowFieldChange(
                                      rowKey,
                                      "source_entity_column_name",
                                      event.target.value
                                    )
                                  }
                                  className={`${tableInputClass} ${
                                    row.is_constant
                                      ? "tw-border-indigo-200 tw-bg-indigo-50 tw-text-indigo-700"
                                      : row.source_entity_column_name
                                      ? ""
                                      : "tw-border-amber-300 tw-bg-amber-50"
                                  }`}
                                >
                                  <option value="">Pick a source column…</option>
                                  <option value={CONSTANT_SOURCE_SENTINEL}>
                                    Custom value (no source column)
                                  </option>
                                  {sourceColumnOptions
                                    .filter((candidate) => candidate.source_entity_column_name)
                                    .map((candidate) => (
                                      <option
                                        key={candidate.source_entity_column_id ?? candidate.source_entity_column_name}
                                        value={candidate.source_entity_column_name ?? ""}
                                      >
                                        {candidate.source_entity_column_name}
                                      </option>
                                    ))}
                                </select>
                                <span
                                  className={`tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide ${
                                    row.is_constant
                                      ? "tw-text-indigo-700"
                                      : "tw-text-emerald-700"
                                  }`}
                                >
                                  {row.is_constant
                                    ? `Constant · ${inheritedRowLabelFor(row)}`
                                    : inheritedRowLabelFor(row)}
                                </span>
                              </div>
                            ) : (
                              <div className="tw-flex tw-flex-col tw-gap-1">
                                <div className="tw-flex tw-h-7 tw-w-full tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-2.5 tw-text-left tw-text-xs tw-text-slate-700">
                                  {row.source_entity_column_name ?? "—"}
                                </div>
                                {isTemplateOwnedColumn ? (
                                  <span
                                    className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-amber-700"
                                    title="The generator emits this column itself — modelling it has no effect on the generated SQL."
                                  >
                                    Emitted by the template
                                  </span>
                                ) : isInheritedBusinessKey ? (
                                  <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-emerald-700">
                                    {inheritedRowLabel}
                                  </span>
                                ) : businessKeyOrder ? (
                                  <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[#03234a]">
                                    Feeds hub hash #{businessKeyOrder}
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </td>
                          <td className="tw-w-48 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
                              <input
                                value={row.target_entity_rdv_column_name ?? ""}
                                onChange={(event) =>
                                  handleRowFieldChange(
                                    rowKey,
                                    "target_entity_rdv_column_name",
                                    event.target.value
                                  )
                                }
                              // Editable: the key's identity is the hub column it records
                              // itself as realising, not this name, so a satellite reading
                              // a different source table may name its copy whatever suits
                              // it. Order and coverage are still checked against the hub.
                              // A stale link key is listed only so it can be taken off.
                              disabled={isStaleLinkKey || isMetadataLocked}
                              className={`${tableInputClass} disabled:tw-bg-slate-50 disabled:tw-text-slate-500`}
                              placeholder="Not mapped"
                            />
                          </td>
                          <td className="tw-w-24 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
                            <BooleanToggle
                              label="Business Key"
                              checked={isBusinessKeyTrue(row.business_key)}
                              onToggle={() =>
                                handleBooleanToggle(rowKey, "business_key", row.business_key)
                              }
                              disabled={businessKeyToggleDisabled || isHashKeyRow || isMetadataLocked}
                            />
                            {/* Only while it is still a key: once switched off it is just a
                                column waiting to be saved, as a satellite's already reads. */}
                            {isStaleKey && isBusinessKeyTrue(row.business_key) ? (
                              <span
                                className="tw-mt-1 tw-block tw-text-[10px] tw-font-semibold tw-leading-tight tw-text-amber-700"
                                title="Its hub no longer has this business key, but it is still hashed here, so the two will not join. Switch it off and save."
                              >
                                {isStaleLinkKey
                                  ? "Not matched to a hub key"
                                  : `Not a key on ${parentHubName || "the parent hub"}`}
                              </span>
                            ) : null}
                          </td>
                          {isHubEntity ? (
                            <td className="tw-w-24 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
                              <BooleanToggle
                                label="Hash Key"
                                checked={isHashKeyRow}
                                onToggle={() =>
                                  handleBooleanToggle(rowKey, "hash_key", row.hash_key)
                                }
                                disabled={isInheritedBusinessKey || isMetadataLocked}
                              />
                            </td>
                          ) : null}
                          {showBusinessKeyForColumn && !isLinkEntity ? (
                            <td className="tw-align-middle tw-px-3 tw-py-3 tw-text-center">
                              {linkBusinessKeyOptions.length ? (
                                <div className="tw-flex tw-flex-col tw-gap-1">
                                  <select
                                    value={
                                      row.business_key_for_target_entity_rdv_id
                                        ? String(row.business_key_for_target_entity_rdv_id)
                                        : ""
                                    }
                                    onChange={(event) =>
                                      handleBusinessKeyForChange(rowKey, event.target.value)
                                    }
                                    disabled={!isBusinessKeyTrue(row.business_key) || isInheritedBusinessKey || isMetadataLocked}
                                    className="tw-h-7 tw-w-full tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-2.5 tw-text-xs tw-text-slate-800 focus:tw-border-[#03234a] focus:tw-outline-none disabled:tw-bg-slate-50 disabled:tw-text-slate-400"
                                  >
                                    <option value="">
                                      {isBusinessKeyTrue(row.business_key)
                                        ? "Select hub"
                                        : "Enable business key"}
                                    </option>
                                    {linkBusinessKeyOptions.map((option) => (
                                      <option key={option.id} value={option.id}>
                                        {option.name}
                                      </option>
                                    ))}
                                  </select>
                                  {isBusinessKeyTrue(row.business_key) &&
                                    !row.business_key_for_target_entity_rdv_id ? (
                                    <p className="tw-text-[10px] tw-text-rose-500">
                                      Select a hub for this business key.
                                    </p>
                                  ) : null}
                                </div>
                              ) : (
                                <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-slate-500">
                                  No linked hubs available
                                </div>
                              )}
                            </td>
                          ) : null}
                          <td className="tw-w-20 tw-align-middle tw-px-3 tw-py-3 tw-text-center">
                              <BooleanToggle
                                label="PII"
                                checked={isTrueFlag(row.pii)}
                                onToggle={() => handleBooleanToggle(rowKey, "pii", row.pii)}
                              disabled={isHashKeyRow || attributeEditLocked || isMetadataLocked}
                            />
                          </td>
                          <td className="tw-w-[12rem] tw-max-w-[12rem] tw-align-middle tw-px-3 tw-py-3 tw-text-center">
                            <button
                              type="button"
                              onClick={() => handleOpenFieldEditor(row, "description", "Description")}
                              className={`tw-flex tw-h-12 tw-w-full tw-items-center tw-rounded-2xl tw-border tw-p-3 tw-text-left ${row.description
                                ? "tw-border-slate-200 tw-bg-white"
                                : "tw-border-dashed tw-border-slate-200 tw-bg-slate-50"
                                } ${descriptionEditLocked ? "tw-cursor-not-allowed tw-bg-slate-100" : "hover:tw-border-[#03234a]"}`}
                              disabled={descriptionEditLocked}
                              title={
                                descriptionEditLocked
                                  ? "Descriptions for fetched column mappings are read-only."
                                  : undefined
                              }
                            >
                              <span className="tw-block tw-w-full tw-truncate tw-text-[11px] tw-text-slate-600">
                                {row.description ? row.description : "Add description"}
                              </span>
                            </button>
                          </td>
                          <td className="tw-w-[18rem] tw-max-w-[18rem] tw-align-top tw-px-3 tw-py-3 tw-text-center">
                            <button
                              type="button"
                              onClick={() =>
                                handleOpenFieldEditor(
                                  row,
                                  "target_entity_rdv_column_transform",
                                  // On a constant row this field is not a transform of
                                  // anything — it IS the value that gets hashed and
                                  // projected. Calling both "Transformation" is what led
                                  // to `where 1=1` being typed here.
                                  row.is_constant ? "Custom value" : "Transformation"
                                )
                              }
                              disabled={
                                (transformLocked && !row.is_canonical_key) ||
                                isHashKeyRow ||
                                isStaleLinkKey ||
                                isMetadataLocked
                              }
                              className={`tw-flex tw-h-12 tw-w-full tw-items-center tw-rounded-2xl tw-border tw-p-3 tw-text-left ${row.target_entity_rdv_column_transform
                                ? "tw-border-slate-200 tw-bg-white"
                                : "tw-border-dashed tw-border-slate-200 tw-bg-slate-50"
                                } ${(transformLocked && !row.is_canonical_key) || isHashKeyRow || isStaleLinkKey || isMetadataLocked ? "tw-cursor-not-allowed tw-bg-slate-100" : "hover:tw-border-[#03234a]"}`}
                            >
                              <div className="tw-block tw-w-full">
                                <SqlPreview
                                  value={row.target_entity_rdv_column_transform ?? ""}
                                  placeholder={
                                    row.is_constant
                                      ? "Set a value, e.g. 'DAHL'"
                                      : "Add transformation"
                                  }
                                />
                              </div>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        className="tw-px-4 tw-py-6 tw-text-center tw-text-xs tw-text-slate-500"
                        colSpan={totalTableColumns}
                      >
                        {showOnlySelectedRows
                          ? "No selected rows to display."
                          : "No column mappings available."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-gap-3">
              <p className="tw-text-xs tw-text-slate-500">
                {/* Count what the grid can list: a link lists its seeds and stale keys only,
                    and a column a seed has taken over is listed once, as the seed. */}
                Showing {displayedRows.length} of{" "}
                {isLinkEntity
                  ? inheritedColumnRows.length + staleLinkKeyRows.length
                  : columnRows.filter((row) => !isSeedClaimed(row)).length +
                    inheritedColumnRows.length}{" "}
                rows
              </p>
            </div>
          </>
        ) : columns.length ? (
          <ColumnCardList columns={columns} />
        ) : (
          <p className="tw-text-sm tw-text-slate-500">
            No source columns were returned for this entity. Ensure mappings are defined in the
            backend service.
          </p>
        )}
        {selectedEntity.type === "link" ? (
          <div className="tw-space-y-3">
            <div>
              <h3 className="tw-text-sm tw-font-semibold tw-text-slate-800">
                Source table lineage
              </h3>
              <p className="tw-text-xs tw-text-slate-500">
                Document the upstream lineage for this link to complete the vault definition.
              </p>
            </div>
            {tableMappings.length ? (
              tableMappings.map((mapping) => (
                <div
                  key={`${mapping.data_source_name}-${mapping.source_table}-${mapping.target_table}`}
                  className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4 tw-shadow-sm"
                >
                  <h3 className="tw-text-sm tw-font-semibold tw-text-slate-800">
                    {mapping.data_source_name || "Unnamed source"}
                  </h3>
                  <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
                    {mapping.source_schema}.{mapping.source_table}
                  </p>
                  <p className="tw-text-xs tw-text-slate-500">
                    → {mapping.target_schema}.{mapping.target_table}
                  </p>
                  {mapping.source_filter ? (
                    <p className="tw-mt-2 tw-text-xs tw-font-medium tw-text-slate-600">
                      Filter: <span className="tw-font-normal">{mapping.source_filter}</span>
                    </p>
                  ) : null}
                  {mapping.Description ? (
                    <p className="tw-mt-2 tw-text-xs tw-text-slate-500">
                      {mapping.Description}
                    </p>
                  ) : null}
                </div>
              ))
            ) : selectedEntity.entity.record.source_entity_name ? (
              // The saved mapping exists but this record's `mappings` array is only filled
              // when the model is loaded from the server, so a link mapped in this session
              // read as having no lineage at all. Show what the record itself knows rather
              // than claiming nothing is recorded.
              <div className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4 tw-shadow-sm">
                <p className="tw-text-sm tw-font-semibold tw-text-slate-800">
                  {selectedEntity.entity.record.source_entity_name}
                </p>
                {selectedEntity.entity.record.data_source_name ? (
                  <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
                    from {selectedEntity.entity.record.data_source_name}
                  </p>
                ) : null}
                {selectedEntity.entity.record.source_filter ? (
                  <p className="tw-mt-2 tw-text-xs tw-font-medium tw-text-slate-600">
                    Filter:{" "}
                    <span className="tw-font-normal">
                      {selectedEntity.entity.record.source_filter}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="tw-text-sm tw-text-slate-500">
                No table mappings recorded for this link yet.
              </p>
            )}
          </div>
        ) : tableMappings.length ? (
          <div className="tw-space-y-3">
            {tableMappings.map((mapping) => (
              <div
                key={`${mapping.data_source_name}-${mapping.source_table}-${mapping.target_table}`}
                className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4 tw-shadow-sm"
              >
                <h3 className="tw-text-sm tw-font-semibold tw-text-slate-800">
                  {mapping.data_source_name || "Unnamed source"}
                </h3>
                <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
                  {mapping.source_schema}.{mapping.source_table}
                </p>
                <p className="tw-text-xs tw-text-slate-500">
                  → {mapping.target_schema}.{mapping.target_table}
                </p>
                {mapping.source_filter ? (
                  <p className="tw-mt-2 tw-text-xs tw-font-medium tw-text-slate-600">
                    Filter: <span className="tw-font-normal">{mapping.source_filter}</span>
                  </p>
                ) : null}
                {mapping.Description ? (
                  <p className="tw-mt-2 tw-text-xs tw-text-slate-500">{mapping.Description}</p>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="tw-text-sm tw-text-slate-500">
            No table mappings recorded for this link. Document the upstream lineage to complete the
            vault definition.
          </p>
        )}
        {fieldEditor ? (
          <div className="tw-absolute tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-slate-900/30 tw-backdrop-blur">
            <div className="tw-w-full tw-max-w-3xl tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-6 tw-shadow-xl">
              <h3 className="tw-text-base tw-font-semibold tw-text-slate-900">
                Edit {fieldEditor.label.toLowerCase()} · {fieldEditor.title}
              </h3>
              <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
                {fieldEditor.field === "target_entity_rdv_column_transform"
                  ? "Paste or type any SQL expression. It will be saved with this column mapping."
                  : "Provide descriptive context for this column mapping."}
              </p>
              {fieldEditor.field === "target_entity_rdv_column_transform" ? (
                <div className="tw-mt-4">
                  <SqlEditorField
                    label="Transform SQL"
                    value={fieldEditor.value}
                    onChange={handleFieldEditorChange}
                    placeholder="SELECT …"
                    height="256px"
                  />
                </div>
              ) : (
                <textarea
                  className="tw-mt-4 tw-h-64 tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4 tw-text-sm tw-text-slate-800 focus:tw-border-slate-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-200"
                  value={fieldEditor.value}
                  onChange={(event) => handleFieldEditorChange(event.target.value)}
                  placeholder="Describe how this column should be used."
                />
              )}
              <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-3">
                <button
                  type="button"
                  onClick={handleFieldEditorCancel}
                  className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-600 hover:tw-bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleFieldEditorSave}
                  className="tw-rounded-full tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#02172f]"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </>
    </ColumnMappingModalShell>
  );
}

function ColumnCardList({ columns }: { columns: ColumnMappingRecord[] }): JSX.Element {
  if (!columns.length) {
    return (
      <p className="tw-text-sm tw-text-slate-500">
        No column mappings documented yet for this table.
      </p>
    );
  }
  return (
    <div className="tw-space-y-3">
      {columns.map((column) => (
        <div
          key={`${column.source_table}-${column.source_column}-${column.target_column}`}
          className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4 tw-shadow-sm"
        >
          <header className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
            <h3 className="tw-text-sm tw-font-semibold tw-text-slate-800">
              {column.source_column} → {column.target_column || "N/A"}
            </h3>
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500">
              <span>BK: {column.business_key || "N"}</span>
              <span>Hash: {column.hash_key || "N"}</span>
              <span>PII: {column.PII || "N"}</span>
            </div>
          </header>
          <p className="tw-mt-2 tw-text-xs tw-text-slate-500">
            {column.source_schema}.{column.source_table}.{column.source_column}
          </p>
          {column.Transformation ? (
            <p className="tw-mt-2 tw-text-xs tw-font-medium tw-text-slate-600">
              Transformation: <span className="tw-font-normal">{column.Transformation}</span>
            </p>
          ) : null}
          {column.Description ? (
            <p className="tw-mt-2 tw-text-xs tw-text-slate-500">{column.Description}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function BooleanToggle({
  label,
  checked,
  onToggle,
  disabled = false
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="tw-flex tw-w-full tw-justify-center">
      <button
        type="button"
        aria-label={label}
        aria-pressed={checked}
        onClick={disabled ? undefined : onToggle}
        disabled={disabled}
        aria-disabled={disabled}
        className={`tw-relative tw-inline-flex tw-h-5 tw-w-10 tw-items-center tw-rounded-full tw-bg-slate-300 tw-transition ${disabled ? "tw-cursor-not-allowed tw-opacity-60" : ""
          }`}
        style={{ backgroundColor: checked ? "#03234a" : undefined }}
        title={disabled ? "Inherited from hub business keys" : undefined}
      >
        <span
          className={`tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-shadow tw-transition ${checked ? "tw-translate-x-5" : "tw-translate-x-1"
            }`}
        />
      </button>
    </div>
  );
}
